import { useState } from 'react'

import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

type Feature = { name: string; desc: string }
type FaqItem = { q: string; a: string }
type SpecItem = {
  id: string
  menu: string
  path?: string
  overview: string
  features: Feature[]
  faq: FaqItem[]
}
type SpecGroup = { group: string; items: SpecItem[] }

const MENU_SPECS: SpecGroup[] = [
  {
    group: 'グローバル管理',
    items: [
      {
        id: 'dashboard',
        menu: 'ダッシュボード',
        path: '/dashboard',
        overview: '全サイト横断の統計情報を確認するメイン画面です。管理者のみアクセス可能です。',
        features: [
          {
            name: 'KPI表示',
            desc: '登録サイト数・ユーザー数・商品数・イベント数・レコメンド数の5つの主要KPIをカード形式で表示します。'
          },
          {
            name: 'イベント種別チャート',
            desc: 'view / view_product / cart / order の各イベントの発生件数をバーチャートで可視化します。'
          },
          {
            name: 'アルゴリズム性能',
            desc: '協調フィルタリング・ニューラル・コンテンツベースなど、レコメンドアルゴリズムごとの利用割合をパイチャートで表示します。'
          },
          {
            name: 'モデル再学習',
            desc: '「モデルを再学習」ボタンで全レコメンドエンジンの再学習を手動実行できます。学習結果（アイテム数・学習時間）が表示されます。'
          }
        ],
        faq: [
          {
            q: 'KPIが0のまま更新されない',
            a: 'タグ設置とデータ取得が正しく行われているか確認してください。サイト別ダッシュボードでデータ有無を確認できます。'
          },
          {
            q: 'モデル再学習が失敗する',
            a: '商品データが0件の場合は学習できません。先に商品マスタを登録してください。'
          }
        ]
      },
      {
        id: 'sites',
        menu: 'サイト管理',
        path: '/sites',
        overview:
          'サイト（ECサイト）の登録・編集・削除を行う画面です。FTP接続情報やサイトアカウントも管理します。管理者のみアクセス可能です。',
        features: [
          {
            name: 'サイト一覧',
            desc: '登録済みサイトの一覧をカード形式で表示します。サイト名・ドメイン・サイトID・APIキー・レコメンド配信率などの基本情報が確認できます。'
          },
          {
            name: 'サイト新規作成',
            desc: 'サイト名、ドメイン、テストドメイン（任意）、案件番号（任意）を入力してサイトを作成します。サイトIDとAPIキーは自動生成されます。'
          },
          {
            name: 'サイト編集',
            desc: 'サイト名・ドメイン・テストドメイン・案件番号・行動分析上限・レコメンド配信率（0%=停止〜100%=全配信）を編集できます。サイトIDは変更不可です。'
          },
          {
            name: 'サイト削除',
            desc: 'サイトを削除すると、紐づく商品・ウィジェット・行動データなどすべて削除されます。確認ダイアログが表示されます。'
          },
          {
            name: 'サイトID',
            desc: '12文字の英数字で自動生成されます。タグ設置やAPI連携に必要な識別子で、変更はできません。'
          },
          {
            name: 'FTP接続情報',
            desc: 'サイトごとにFTP連携の有効/無効を切り替えます。有効化するとFTPユーザー名・パスワードが自動生成され、接続情報（ホスト・ポート・ユーザー名・パスワード）を確認できます。FTPホストIPはシステム設定で変更可能です。'
          },
          {
            name: '接続情報コピー',
            desc: 'FTP接続情報（ホスト・ポート・ユーザー名・パスワード・利用手順）をワンクリックでクリップボードにコピーし、ECサイト担当者へ案内できます。'
          },
          {
            name: 'FTPパスワード再生成',
            desc: 'FTPパスワードを再生成できます。再生成後はEC側への再連絡が必要です。確認ダイアログが表示されます。'
          },
          {
            name: 'サイトアカウント',
            desc: 'サイトごとにサイト管理画面用のログインアカウントを作成できます。ログインID・パスワード・メールアドレスを設定し、作成後に認証情報をコピーしてEC担当者へ共有できます。'
          }
        ],
        faq: [
          {
            q: 'サイトIDを変更したい',
            a: 'サイトIDは変更できません。別のサイトとして新規作成してください。'
          },
          {
            q: 'ドメインとテストドメインの違い',
            a: 'ドメインは本番サイトURL、テストドメインはステージング環境URLです。テストドメインからの受注データは「テスト」フラグが付きます。'
          },
          {
            q: 'FTP接続情報のホストIPを変更したい',
            a: 'グローバル管理の「システム設定」画面でFTPサーバーIPを変更できます。変更は全サイトの接続情報コピーに反映されます。'
          },
          {
            q: 'FTPパスワードを確認したい',
            a: 'FTP接続情報セクションの「表示」ボタンでパスワードを表示できます。セキュリティのためデフォルトは非表示です。'
          }
        ]
      },
      {
        id: 'analytics',
        menu: 'アナリティクス',
        path: '/analytics',
        overview:
          '全サイト横断のA/Bテスト結果・アルゴリズム性能を分析する画面です。管理者のみアクセス可能です。',
        features: [
          {
            name: 'A/Bテスト結果',
            desc: 'A/Bテストグループ別のCTR（クリック率）をバーチャートとテーブルで比較表示します。'
          },
          {
            name: 'アルゴリズム性能比較',
            desc: '各レコメンドアルゴリズム（協調フィルタリング、ニューラル等）のCTRを比較します。レコメンド数・クリック数も確認できます。'
          }
        ],
        faq: [
          {
            q: 'データが表示されない',
            a: 'レコメンドの配信実績がない場合はデータが表示されません。ウィジェットを設置し、一定量のアクセスがあるまでお待ちください。'
          }
        ]
      },
      {
        id: 'operators',
        menu: 'オペレーター管理',
        path: '/operators',
        overview:
          '管理画面にログインするオペレーターの登録・編集を行う画面です。管理者のみアクセス可能です。',
        features: [
          {
            name: 'オペレーター一覧',
            desc: '登録済みオペレーターの一覧（名前・ログインID・権限・担当サイト）を表示します。'
          },
          {
            name: 'オペレーター作成',
            desc: 'ログインID・パスワード・名前を設定して新規作成します。「全体管理者」チェックで管理者権限を付与できます。'
          },
          {
            name: '権限設定',
            desc: '「全体管理者」はダッシュボード・サイト管理・アナリティクス等の全機能にアクセスできます。通常オペレーターは割り当てサイトのみ操作可能です。'
          },
          {
            name: 'サイト割り当て',
            desc: '通常オペレーターには担当サイトを1つ割り当てます。割り当てサイト以外のデータにはアクセスできません。'
          },
          {
            name: 'サイトアカウント一覧',
            desc: '各サイトのサイトアカウント（サイト別管理画面用）の一覧が確認できます。'
          }
        ],
        faq: [
          {
            q: 'パスワードを忘れた',
            a: 'オペレーター管理画面から該当アカウントのパスワードを再設定できます（管理者権限が必要）。'
          },
          {
            q: 'サイトオペレーターが他サイトを見えてしまう',
            a: 'サイト割り当てが正しく設定されているか確認してください。「全体管理者」にチェックが入っていると全サイトが見えます。'
          }
        ]
      },
      {
        id: 'system-config',
        menu: 'システム設定',
        path: '/system-config',
        overview:
          'メール通知やFTPホストなど、システム全体の設定を管理する画面です。管理者のみアクセス可能です。',
        features: [
          {
            name: 'メール通知設定',
            desc: 'FTP取込みエラー時などに送信される通知メールのSMTP設定です。SMTPサーバー・ポート・送信元メールアドレス・SMTPパスワード・通知先メールアドレスを設定します。'
          },
          {
            name: 'FTPサーバー設定',
            desc: 'サイト管理のFTP接続情報コピーで使用されるFTPサーバーIPアドレスを設定します。変更は全サイトの接続情報に反映されます。'
          },
          {
            name: '設定の保存',
            desc: '「設定を保存」ボタンで全設定を一括保存します。保存成功時はメッセージが表示されます。'
          },
          {
            name: 'デフォルト値',
            desc: 'SMTPサーバーは「smtp.gmail.com」、SMTPポートは「587」、FTPサーバーIPは「64.176.52.160」がデフォルト値です。未設定の場合は環境変数の値にフォールバックします。'
          }
        ],
        faq: [
          {
            q: 'Gmailで通知メールを送りたい',
            a: 'SMTPサーバーに「smtp.gmail.com」、ポートに「587」を設定し、Googleアカウントの「アプリパスワード」をSMTPパスワードに入力してください。通常のGoogleパスワードではログインできません。'
          },
          {
            q: '設定を変更しても通知が届かない',
            a: '送信元メールアドレス・SMTPパスワード・通知先メールアドレスの3つすべてが設定されていないと通知は送信されません。空欄がないか確認してください。'
          },
          {
            q: 'FTPホストIPを変更した場合の影響',
            a: 'サイト管理画面の「接続情報コピー」に含まれるホストIPが変更後の値になります。既にEC側へ案内済みの場合は再連絡が必要です。'
          }
        ]
      },
      {
        id: 'proposal',
        menu: 'サービス紹介資料',
        path: '/proposal',
        overview:
          'W2 Smart Hubの各サービス（Reco-Media / Media-DRIVER / Findee）の概要・機能紹介をプレゼンテーション形式で表示する画面です。営業提案時に利用します。',
        features: [
          {
            name: 'サービス別スライド',
            desc: 'Reco-Media（/proposal）・Media-DRIVER（/proposal2）・Findee（/proposal3）の3種の提案資料をスライド形式で表示します。'
          },
          {
            name: 'ナビゲーション',
            desc: '左右ボタンまたはキーボードの矢印キーでスライドを移動できます。'
          }
        ],
        faq: []
      },
      {
        id: 'audit-logs',
        menu: '監査ログ',
        path: '/audit-logs',
        overview:
          'システム全体の操作ログを表示・検索する画面です。誰がいつ何を操作したかを追跡できます。管理者のみアクセス可能です。',
        features: [
          { name: '操作ログ一覧', desc: '操作者・対象・操作種別・日時の記録を一覧表示します。' },
          { name: '検索・フィルタ', desc: '操作者や対象、期間で絞り込めます。' }
        ],
        faq: [
          {
            q: 'どんな操作が記録されますか？',
            a: '主要なデータ変更操作が記録されます。詳細は記録対象の実装に依存します。'
          }
        ]
      },
      {
        id: 'site-usage',
        menu: 'サイト状況',
        path: '/site-usage',
        overview:
          '各サイトの利用状況サマリーを一覧する画面です。API呼び出し数・データ件数・機能利用フラグ・ヘルススコアを確認できます。管理者のみアクセス可能です。',
        features: [
          {
            name: '利用状況サマリー',
            desc: 'サイトごとのAPI呼び出し数・データ件数・有効機能フラグを一覧表示します。'
          },
          { name: 'ヘルススコア', desc: 'サイトの利用健全性をスコアで可視化します。' }
        ],
        faq: [
          {
            q: 'ダッシュボードのサービス別サマリーとの違いは？',
            a: 'ダッシュボードはサービス横断の集計、サイト状況はサイト単位の利用状況に焦点を当てています。'
          }
        ]
      },
      {
        id: 'billing',
        menu: '請求管理',
        path: '/billing',
        overview:
          'サイト別の月別請求と、サイト単位のサービス利用状況を管理する画面です。「請求一覧」と「サービス利用状況」の2タブ構成です。管理者のみアクセス可能です。',
        features: [
          {
            name: '請求一覧タブ',
            desc: 'サイト別月別請求の作成・編集・ステータス管理（draft/confirmed/invoiced/paid）を行います。'
          },
          {
            name: 'サービス利用状況タブ',
            desc: '月選択式で、サイト×サービスの月次利用量を表で表示します。'
          },
          {
            name: 'Reco-Media 2指標',
            desc: 'タグデータ送信数（行動イベント）とレコメンド配信数を分けて表示します。'
          },
          {
            name: 'Media-DRIVER 2指標',
            desc: '記事生成数（記事バージョン）とアップロード数（アップロード申請）を分けて表示します。'
          },
          {
            name: '各サービス利用量',
            desc: '連動データ・GEO/LLMO・UGC Intelligence・Findee・カルテ の月次利用量を一覧表示します。'
          }
        ],
        faq: [
          {
            q: 'タグデータ送信数とは何ですか？',
            a: 'タグから送信された行動イベント（閲覧/カート/購入）の件数です。レコメンド配信数（配信ログ件数）とは別の指標です。'
          },
          {
            q: '集計対象月は変えられますか？',
            a: 'サービス利用状況タブの月セレクタで直近12ヶ月から選択できます。'
          }
        ]
      }
    ]
  },
  {
    group: '💎 カルテ（Valuable data）',
    items: [
      {
        id: 'customers',
        menu: '顧客カルテ',
        path: '/sites/:siteId/recommend/customers',
        overview:
          'クッキーベースの訪問者データを一覧表示し、個別の行動分析とAIインサイトを提供する画面です。',
        features: [
          {
            name: '訪問者一覧',
            desc: 'クッキーID・顧客ID・デバイス・配信数・各アクション回数（view/view_product/cart/order）・初回/最終アクセス日を表示します。'
          },
          {
            name: 'ステータスフィルタ',
            desc: '「すべて」「会員」（顧客ID紐づき済み）「不明ユーザー」（顧客IDなし）でフィルタリングできます。'
          },
          {
            name: 'ソート機能',
            desc: '配信数・View数・Cart数・Order数・初回アクセス・最終アクセスなどでソートできます。'
          },
          {
            name: '行動分析モーダル',
            desc: '顧客をクリックすると、セッション別のタイムライン形式で全行動履歴が表示されます。30分以上の間隔でセッション区切りされます。'
          },
          {
            name: 'AI インサイト',
            desc: '行動分析モーダル内の「AIインサイト」ボタンで、顧客の購買パターン・行動傾向をAIが自動分析してコメントします。'
          },
          { name: 'デバイス比率', desc: 'PC/モバイル/タブレットの利用比率を円グラフで表示します。' }
        ],
        faq: [
          {
            q: '顧客カルテにデータが表示されない',
            a: 'サイトにタグが設置され、行動データが取得されていることが前提です。「顧客カルテオプション」が有効かタグ設定画面で確認してください。'
          },
          {
            q: '会員と不明ユーザーの違い',
            a: '購入時に顧客IDが紐づいた訪問者が「会員」、紐づいていない訪問者が「不明ユーザー」です。'
          }
        ]
      },
      {
        id: 'ab-campaigns',
        menu: '配信内容決定（A/Bテスト）',
        path: '/sites/:siteId/ab-campaigns',
        overview: 'ウィジェットのA/Bテストキャンペーンを作成・実行・分析する画面です。',
        features: [
          {
            name: 'キャンペーン作成',
            desc: 'A/B対応ウィジェット（ab_enabled=true）を選択してキャンペーンを作成します。KPI目標（CTR or CVR）、ターゲットデバイス、信頼度（90/95/99%）、最小サンプル数を設定します。'
          },
          {
            name: 'キャンペーン実行',
            desc: '「開始」ボタンでキャンペーンを実行状態にします。実行中はウィジェットの設定変更を避けてください。'
          },
          {
            name: '統計分析',
            desc: 'パターン毎のCTR/CVR・サンプル数・p値を表示します。統計的有意差がある場合、勝者パターンが自動判定されます。'
          },
          {
            name: 'サンプル充足度',
            desc: '設定した最小サンプル数に対する現在のサンプル充足度をプログレスバーで表示します。'
          },
          {
            name: '累積推移グラフ',
            desc: '日別のCTR/CVR推移をパターン毎の折れ線グラフで表示します。'
          },
          {
            name: 'キャンペーン終了',
            desc: '「完了」ボタンでキャンペーンを終了します。結果はそのまま閲覧可能です。'
          }
        ],
        faq: [
          {
            q: 'A/Bテスト対象のウィジェットが選択肢に出ない',
            a: 'ウィジェット設定画面で「A/Bテスト有効」をONにしてください。パターン数とトラフィック配分も設定が必要です。'
          },
          {
            q: '有意差が出ない',
            a: 'サンプル数が不足しています。十分なアクセス数が集まるまでお待ちください。信頼度を90%に下げることで判定しやすくなります。'
          }
        ]
      }
    ]
  },
  {
    group: '📦 コンテンツ管理',
    items: [
      {
        id: 'products',
        menu: '商品管理',
        path: '/sites/:siteId/products',
        overview:
          'ECサイトの商品マスタデータを登録・管理する画面です。レコメンドエンジンの学習データとして使用されます。',
        features: [
          {
            name: '商品一覧',
            desc: '商品ID・名前・カテゴリ・サブカテゴリ・価格・画像URL・商品URLなどを一覧表示します。20件/ページのページングです。'
          },
          {
            name: '商品登録',
            desc: '商品名・カテゴリ・サブカテゴリ・説明・価格・外部ID・画像URL・商品URLを入力して個別登録します。'
          },
          {
            name: 'CSV一括アップロード',
            desc: 'CSVファイルで一括登録・更新（UPSERT）できます。必須列: name, external_id。任意列: category, subcategory, description, price, image_url, product_url, tags。同じexternal_idの商品が既存の場合は上書き更新されます。UTF-8/Shift_JIS対応。'
          },
          {
            name: '検索・フィルタ',
            desc: '簡易検索（商品名）と詳細検索（外部ID、名前、説明、カテゴリ、価格範囲、URL、タグ）に対応。カテゴリフィルタドロップダウンあり。'
          },
          {
            name: 'ソート',
            desc: '各列ヘッダーをクリックしてソートできます（名前、カテゴリ、価格等）。'
          },
          {
            name: '一括削除',
            desc: '「全商品削除」ボタンで全データを削除できます（確認ダイアログあり）。'
          }
        ],
        faq: [
          {
            q: 'CSVアップロードでエラーになる',
            a: '必須列（name, external_id）が含まれているか確認してください。priceは数値のみ（カンマ不要）です。'
          },
          {
            q: '外部IDとは何ですか',
            a: 'ECサイト側の商品IDです。タグで送信されるitem_idと一致させることで、商品情報とレコメンドが紐づきます。'
          }
        ]
      },
      {
        id: 'categories',
        menu: 'カテゴリマスタ',
        path: '/sites/:siteId/categories',
        overview:
          '商品カテゴリの階層構造を管理する画面です。最大9階層までの親子関係を設定できます。',
        features: [
          {
            name: 'カテゴリ一覧',
            desc: 'カテゴリID・カテゴリ名・親カテゴリID・階層レベルを表示します。50件/ページのページングです。'
          },
          {
            name: 'カテゴリ作成',
            desc: 'カテゴリID（英数字・ハイフン・アンダースコア）、カテゴリ名、親カテゴリID（任意）、階層レベル（1〜9）を設定します。'
          },
          {
            name: 'インライン編集',
            desc: 'テーブル上で直接カテゴリ名・親カテゴリID・階層レベルを編集できます。'
          },
          {
            name: 'CSV一括アップロード',
            desc: '列: category_id, category_name, parent_category_id, hierarchy_level。既存IDは上書き更新されます。'
          },
          {
            name: '検索・フィルタ',
            desc: 'カテゴリID・カテゴリ名で検索、階層レベルでフィルタリングできます。'
          },
          {
            name: '一括削除',
            desc: '全カテゴリを一括削除できます。商品との紐づけデータも影響を受けるためご注意ください。'
          }
        ],
        faq: [
          {
            q: 'カテゴリIDに使える文字',
            a: '半角英数字、ハイフン（-）、アンダースコア（_）のみです。日本語やスペースは使用できません。'
          },
          {
            q: '階層レベルの意味',
            a: '1がトップレベル（大分類）、数値が大きいほど下位階層です。親カテゴリIDで親子関係を表現します。'
          }
        ]
      },
      {
        id: 'item-category',
        menu: 'カテゴリ紐づけ',
        path: '/sites/:siteId/item-category',
        overview:
          '商品（アイテムID）とカテゴリの対応関係を管理する画面です。1つの商品に複数カテゴリを紐づけ可能です。',
        features: [
          {
            name: '紐づけ一覧',
            desc: 'アイテムID・カテゴリIDのマッピングを一覧表示します。50件/ページ。'
          },
          {
            name: '個別登録',
            desc: 'アイテムIDとカテゴリIDを指定して紐づけを作成します。重複する組み合わせはエラーになります。'
          },
          {
            name: 'CSV一括アップロード',
            desc: '列: item_id, category_id。重複する組み合わせはスキップされます。'
          },
          { name: '検索', desc: 'アイテムIDまたはカテゴリIDで検索できます。' },
          { name: '削除', desc: '個別削除または全削除が可能です。' }
        ],
        faq: [
          {
            q: 'アイテムIDとは',
            a: '商品マスタの外部ID（external_id）と一致するIDです。ECサイト側で使用している商品IDを指定してください。'
          },
          {
            q: '紐づけを変更したい',
            a: '既存の紐づけを削除してから、新しい紐づけを登録してください。'
          }
        ]
      },
      {
        id: 'inventory',
        menu: '在庫管理',
        path: '/sites/:siteId/inventory',
        overview:
          '商品ごとの在庫数を登録・管理する画面です。在庫切れ商品のレコメンド除外に利用されます。',
        features: [
          {
            name: '在庫一覧',
            desc: '商品ID・商品名・在庫数・更新日時を表示します。在庫状況はカラーバッジで表示（0=赤、1〜10=オレンジ、11以上=緑）。'
          },
          {
            name: '在庫登録/更新',
            desc: '商品IDと在庫数を指定して登録します。既存の商品IDの場合は在庫数が上書き更新されます。'
          },
          {
            name: 'CSV一括アップロード',
            desc: '列: item_id, quantity。既存の商品IDは上書き更新されます。'
          },
          { name: 'インライン編集', desc: 'テーブル上で直接在庫数を変更できます。' },
          { name: '検索', desc: '商品IDで検索できます。' }
        ],
        faq: [
          {
            q: '在庫0の商品はレコメンドに出ますか',
            a: '在庫管理に登録し在庫0の商品はレコメンド候補から除外されます。在庫管理に未登録の商品は在庫ありとして扱われます。'
          },
          {
            q: 'リアルタイムで在庫更新したい',
            a: 'APIで在庫を更新できます（POST /api/sites/{site_id}/inventory）。ECサイトの在庫変動時にAPI連携してください。'
          }
        ]
      },
      {
        id: 'file-upload',
        menu: 'ファイル取込設定',
        path: '/sites/:siteId/file-upload',
        overview:
          'CSVファイルの手動アップロードとFTP経由の自動取込みを管理する画面です。FTP自動取込みの有効/無効状態の確認や、オペレーター別メール通知のON/OFF設定も行えます。',
        features: [
          {
            name: 'タブ構成',
            desc: '「手動取込み」「CSVフォーマット」「取込実行ログ」「FTP連携ログ」の4つのタブで構成されています。'
          },
          {
            name: 'FTP自動取込バッジ',
            desc: 'ページタイトル横にFTP自動取込のON/OFFバッジが表示されます。サイト管理画面で設定を変更できます。'
          },
          {
            name: '手動取込み',
            desc: 'CSVファイルをドラッグ&ドロップまたはファイル選択でアップロードします。マスタ種別（商品/カテゴリ/紐づけ/在庫/受注）はファイルのヘッダー行から自動判定されます。'
          },
          {
            name: 'CSVフォーマット',
            desc: '各マスタ種別のCSV列仕様（列名・型・必須/任意・説明）と、サンプルCSVのダウンロード機能を提供します。UTF-8/Shift_JIS対応。'
          },
          {
            name: '取込実行ログ',
            desc: 'アップロード履歴を一覧表示します。ファイル名・マスタ種別・取込元（API/FTP/手動）・ステータス（待機中/処理中/完了/失敗）・処理件数（作成/更新/スキップ/エラー）が確認できます。エラー件数の横の「*」をクリックすると、行番号ごとのエラー詳細をポップアップで確認できます。'
          },
          {
            name: 'リトライ',
            desc: '失敗した取込みを「リトライ」ボタンで再実行できます。元のCSVデータが最大5世代まで保持されており、そこから再処理されます。'
          },
          {
            name: 'FTP連携ログ',
            desc: 'FTP中継サーバー経由で取り込まれたファイルの処理状況を一覧表示します。ステータス（転送済/リトライ中/失敗）と詳細メッセージが確認できます。'
          },
          {
            name: 'FTP中継ステータス',
            desc: 'FTP中継サーバーの稼働状況（稼働中/応答遅延/停止）をリアルタイムに表示します。最終チェック時刻とリトライ待ちファイル数も確認できます。'
          },
          {
            name: 'メール通知設定',
            desc: 'オペレーターごとに通知メールのON/OFFを切り替えられます。メールアドレスの入力も可能です。FTP取込み失敗時に、有効なオペレーターとシステム設定の通知先に通知メールが送信されます。'
          }
        ],
        faq: [
          {
            q: 'FTP取込みが失敗する',
            a: 'CSVのフォーマット（ヘッダー行・列数・データ型）が正しいか確認してください。「CSVフォーマット」タブで仕様を確認し、サンプルCSVを参考にしてください。失敗時は最大3回まで自動リトライされます。'
          },
          {
            q: '取込みの通知メールが届かない',
            a: 'グローバル管理の「システム設定」画面でSMTP設定と通知先メールアドレスが正しく設定されているか確認してください。3項目すべてが設定されていないと通知は送信されません。'
          },
          {
            q: 'FTP中継サーバーが停止と表示される',
            a: '中継サーバーが正常に動作していない可能性があります。サーバーの稼働状況を確認してください。'
          },
          {
            q: '手動取込みとFTP取込みの違い',
            a: '手動取込みは管理画面からCSVをアップロードする方式、FTP取込みはEC側からFTPでファイルを送信し自動処理される方式です。定期的なデータ連携にはFTPが便利です。'
          }
        ]
      }
    ]
  },
  {
    group: '📈 取得データ',
    items: [
      {
        id: 'delivery-stats',
        menu: '配信履歴',
        path: '/sites/:siteId/recommend/dashboard/delivery-stats',
        overview:
          'レコメンドウィジェットの配信成果（表示・クリック・コンバージョン・売上）を分析する画面です。',
        features: [
          { name: '期間選択', desc: '7日/14日/30日/90日の期間を選択して集計データを表示します。' },
          {
            name: 'KPI表示',
            desc: '総配信数・クリック数・CV数・売上金額を大きなカードで表示します。'
          },
          {
            name: '日別推移チャート',
            desc: '表示数・クリック数・CV数の日別推移をバーチャートで表示します。'
          },
          {
            name: 'ウィジェット別実績',
            desc: '各ウィジェットの配信数・クリック数・CTR・CV数・CVR・売上を表形式で表示します。'
          },
          {
            name: 'A/Bテスト内訳',
            desc: 'A/Bテスト有効なウィジェットでは、パターン別（A/B/C...）の成績内訳が表示されます。'
          }
        ],
        faq: [
          {
            q: 'CTRが低い',
            a: 'ウィジェットのデザイン（CSS）やレイアウト設定を見直してください。A/Bテストで最適なデザインを検証することをお勧めします。'
          },
          {
            q: '売上が0円',
            a: '購入完了タグが正しく設置されているか確認してください。タグからorder_numberとitem_idが送信される必要があります。'
          }
        ]
      },
      {
        id: 'order-history',
        menu: '受注履歴',
        path: '/sites/:siteId/recommend/order-history',
        overview:
          '受注データの管理・閲覧と、商品別ランキング分析を行う画面です。CSVインポートとタグ経由の両方のデータを管理します。',
        features: [
          {
            name: 'タブ切り替え',
            desc: '「受注履歴」（データ一覧）と「商品ランキング」（ランキング分析）の2つのタブがあります。'
          },
          {
            name: '受注一覧',
            desc: '受注日時・顧客特定番号・受注番号・アイテムID・単価・購入数量・取得元（CSV/タグ）を表示します。本番/テストのフィルタ付き。'
          },
          {
            name: 'CSV取込み',
            desc: '受注データをCSVファイルからインポート（UPSERT）します。必須列: order_datetime, customer_id, order_number, item_id。任意列: unit_price, quantity。同じ受注番号+アイテムIDの組み合わせが既存の場合は上書き更新されます。'
          },
          {
            name: '環境フィルタ',
            desc: '「すべて」「本番」「テスト」で受注データをフィルタリングできます。テストドメイン経由のデータはテストフラグが付きます。'
          },
          {
            name: '商品ランキング',
            desc: '日別/週別/月別で商品IDごとの販売数・受注数・売上を集計し、3期間分のランキングを横並びで表示します。前期間との順位変動も表示されます。'
          },
          {
            name: 'ランキング環境フィルタ',
            desc: '商品ランキングでも本番/テストのフィルタが利用でき、本番データのみでランキングを確認できます。'
          },
          {
            name: 'AI分析',
            desc: '「AIで分析」ボタンで、ランキングデータに基づく売上トレンド・急上昇/急降下商品・改善提案をAIが自動生成します。'
          },
          {
            name: '検索・詳細検索',
            desc: '受注番号・アイテムID・顧客IDで検索できます。詳細検索では受注日時・顧客特定番号・受注番号・アイテムIDの個別フィルタが可能です。'
          }
        ],
        faq: [
          {
            q: 'CSVインポートでエラーになる',
            a: '必須列（order_datetime, customer_id, order_number, item_id）が含まれているか確認してください。受注日時の形式（YYYY/MM/DD HH:MM:SS）と、単価・数量が数値のみであることも確認してください。同じ受注番号+アイテムIDの組み合わせは上書き更新されます。'
          },
          {
            q: '本番とテストの区別',
            a: 'タグ経由のデータはドメインで自動判別されます。サイト設定のテストドメインと一致する場合「テスト」フラグが付きます。CSVインポートは常に本番扱いです。'
          }
        ]
      },
      {
        id: 'behavior',
        menu: '行動履歴',
        path: '/sites/:siteId/recommend/behavior',
        overview:
          'タグから取得したユーザー行動データの一覧表示と、ページURLランキング分析を行う画面です。',
        features: [
          {
            name: 'タブ切り替え',
            desc: '「行動ログ」（データ一覧）と「ページランキング」（ランキング分析）の2つのタブがあります。'
          },
          {
            name: '行動ログ一覧',
            desc: 'イベント日時・アクション種別・クッキーID・セッションID・顧客ID・商品ID・カテゴリID・デバイス・ブラウザ・ページURL・リファラーを表示します。'
          },
          {
            name: 'アクションフィルタ',
            desc: 'view / view_product / cart / order のアクション種別でフィルタリングできます。'
          },
          {
            name: '検索・詳細検索',
            desc: '簡易検索（全フィールド横断）と詳細検索（クッキーID、商品ID、カテゴリID、顧客ID、期間）に対応しています。'
          },
          {
            name: 'ページランキング',
            desc: '日別/週別/月別でページURLごとのPV（ページビュー）・UU（ユニークユーザー）を集計し、3期間分のランキングを横並びで表示します。'
          },
          {
            name: '順位変動',
            desc: '前期間との順位変化が色付きバッジ（緑=上昇、赤=下降、灰=維持、黄=NEW）で表示されます。'
          },
          {
            name: 'AI分析',
            desc: '「AIで分析」ボタンで、ランキングデータに基づくトレンド変化・急上昇/急降下ページの要因推測・改善提案をAIが自動生成します。'
          }
        ],
        faq: [
          {
            q: '行動データが取得されない',
            a: 'タグが正しく設置されているか確認してください。ブラウザのコンソールでタグの送信エラーがないか確認することもお勧めします。'
          },
          {
            q: 'PVとUUの違い',
            a: 'PV（ページビュー）は同一ユーザーの重複アクセスも含む総閲覧数、UU（ユニークユーザー）はクッキーIDで重複を除いた訪問者数です。'
          }
        ]
      }
    ]
  },
  {
    group: '⚙️ 配信・実装設定',
    items: [
      {
        id: 'setup-guide',
        menu: '設定ガイド',
        path: '/sites/:siteId/recommend/setup-guide',
        overview:
          'サイトの導入状況をチェックリスト形式で確認する画面です。5段階のフェーズで進捗を管理します。',
        features: [
          { name: '全体進捗バー', desc: '全ステップの完了率（%）をプログレスバーで表示します。' },
          {
            name: 'フェーズ1: コンテンツ準備',
            desc: '商品マスタ登録・カテゴリマスタ登録・カテゴリ紐づけ・受注データ取り込みの状況を確認します。'
          },
          {
            name: 'フェーズ2: 配信タグ設置',
            desc: '基本タグ・商品閲覧タグ・カートタグ・購入完了タグ・メール用タグの設置状況を確認します。'
          },
          { name: 'フェーズ3: ウィジェット作成', desc: 'ウィジェットの作成件数を確認します。' },
          {
            name: 'フェーズ4: データ取得確認',
            desc: '行動データ・受注データの取得状況を確認します。'
          },
          {
            name: 'フェーズ5: 分析・活用',
            desc: 'ジャーニー分析・配信履歴の活用状況を確認します。'
          },
          { name: 'クイックリンク', desc: '各ステップから該当する設定画面に直接遷移できます。' }
        ],
        faq: [
          {
            q: '進捗が100%にならない',
            a: '各フェーズのステップカードを確認し、未完了の項目を対応してください。データ件数が0の場合は未完了扱いになります。'
          }
        ]
      },
      {
        id: 'widgets',
        menu: 'ウィジェット',
        path: '/sites/:siteId/recommend/widgets',
        overview:
          'レコメンド表示用ウィジェットの作成・編集を行う画面です。表示ロジック・デザイン・A/Bテストを設定します。',
        features: [
          {
            name: 'ウィジェット一覧',
            desc: '作成済みウィジェットのカード一覧。ウィジェット名・ロジック・レイアウト・Spec ID・ステータスが確認できます。'
          },
          {
            name: 'ウィジェット作成',
            desc: 'ウィジェット名、表示ロジック（BB/OB/Personal/Similar/Mail）、レイアウト（Grid/Carousel/Slider/List）、表示件数、タイトルを設定します。'
          },
          {
            name: '表示ロジック',
            desc: 'BB=この商品を買った人はこちらも購入（軸商品のitem_idが必須。商品詳細ページ向け）、OB=この商品を見た人はこちらを購入（軸商品のitem_idが必須。商品詳細ページ向け）、Personal=訪問者の行動履歴に基づくパーソナライズ（item_id不要。トップページ向け）、Similar=コンテンツベースの類似商品（軸商品のitem_idが必須。商品詳細ページ向け）、Mail=メール配信用。'
          },
          {
            name: 'Spec ID',
            desc: 'タグで指定するウィジェット識別子です（例: RECOMMEND_01）。タグ設置時にこのIDを使用します。'
          },
          {
            name: 'AI CSS生成',
            desc: 'ECサイトURLを入力すると、サイトのデザインに合わせたCSSをAIが自動生成します。Basic認証にも対応しています。'
          },
          {
            name: 'カスタムCSS',
            desc: '手動でCSSを編集できます。ビジュアルエディタとコードエディタの2モードがあります。'
          },
          {
            name: 'A/Bテスト設定',
            desc: 'パターン数（2〜4）とトラフィック配分を設定します。パターンごとにデザインを変更できます。'
          },
          {
            name: 'デザインプレビュー',
            desc: 'プレビューボタンで実際の表示イメージを確認できます。'
          },
          {
            name: 'ターゲットURL',
            desc: '対象ページのURLを指定してデザインプレビューに使用します。'
          }
        ],
        faq: [
          {
            q: 'BB / OB / Personal / Similar の違い',
            a: 'BB=この商品を買った人が他に購入した商品（item_id必須・商品詳細ページ向け）、OB=この商品を見た人が購入した商品（item_id必須・商品詳細ページ向け）、Personal=訪問者の閲覧・購入履歴に基づく個人向けレコメンド（item_id不要・トップページ向け）、Similar=TF-IDF類似度による類似商品（item_id必須・商品詳細ページ向け）。'
          },
          {
            q: 'CSSが反映されない',
            a: 'ブラウザのキャッシュをクリアしてください。また、CSSの優先度（specificity）の問題で、ECサイト側のCSSに上書きされている可能性があります。'
          },
          {
            q: 'Spec IDの命名規則',
            a: '英数字とアンダースコアが使用できます。サイト内でユニークである必要があります（例: RECOMMEND_TOP, RECOMMEND_DETAIL）。'
          }
        ]
      },
      {
        id: 'tags',
        menu: '配信タグ情報',
        path: '/sites/:siteId/recommend/tags',
        overview:
          'ECサイトに設置するトラッキングタグとレコメンド配信タグを生成・管理する画面です。',
        features: [
          {
            name: '基本タグ',
            desc: '全ページ共通で設置するJavaScriptタグです。サイトID・ページURL・リファラーを自動送信します。</head>直前に設置します。'
          },
          {
            name: '商品閲覧タグ',
            desc: '商品詳細ページに設置します。item_id（商品ID）とcategory_id（カテゴリID）をパラメータとして送信します。'
          },
          {
            name: 'カテゴリ閲覧タグ',
            desc: 'カテゴリ一覧ページに設置します。category_idをパラメータとして送信します。'
          },
          {
            name: 'カートタグ',
            desc: 'カートページに設置します。item_idとquantity（数量）をパラメータとして送信します。'
          },
          {
            name: '購入完了タグ',
            desc: '注文完了ページに設置します。order_number、item_id、unit_price、quantity、customer_idをパラメータとして送信します。'
          },
          {
            name: 'メール配信用タグ',
            desc: 'API形式のレコメンド取得タグです。visitor_idとwidget_specを指定してレコメンドを取得します。メルマガ等に利用します。'
          },
          {
            name: '顧客カルテオプション',
            desc: 'ONにすると全ページの行動を記録します（有料機能）。OFFの場合はview_product/cart/orderのみ記録します。'
          },
          { name: 'コピー機能', desc: '各タグの「コピー」ボタンでクリップボードにコピーできます。' }
        ],
        faq: [
          {
            q: 'タグはどこに設置しますか',
            a: '基本タグは全ページの</head>直前、各ページ固有タグは該当ページの</body>直前に設置してください。'
          },
          {
            q: 'item_idは何を指定しますか',
            a: '商品マスタに登録した外部ID（external_id）を指定してください。ECサイト側の商品IDと一致させます。'
          },
          {
            q: 'customer_idは何を指定しますか',
            a: '購入者を特定できるID（会員ID等）を指定してください。受注データの顧客特定番号と一致させます。'
          }
        ]
      },
      {
        id: 'model-tuning',
        menu: 'モデルチューニング',
        path: '/sites/:siteId/recommend/model-tuning',
        overview:
          'レコメンドエンジンのアルゴリズムパラメータを調整する画面です。通常はデフォルト値で問題ありません。',
        features: [
          {
            name: 'ハイブリッド重み',
            desc: '協調フィルタリング・ニューラル・コンテンツベースの3つのアルゴリズムの混合比率をスライダーで調整します。合計が1.0になる必要があります。'
          },
          {
            name: '協調フィルタリング設定',
            desc: 'SVD因子数、SVD/KNN比率、KNN近傍数、アクションスコア（view/cart/purchase）を調整できます。'
          },
          {
            name: 'ニューラル(NCF)設定',
            desc: '埋め込み次元数、MLP層構成、エポック数、学習率、バッチサイズ、ドロップアウト率を調整できます。'
          },
          { name: 'コンテンツベース設定', desc: 'TF-IDFの最大特徴量数を調整できます。' },
          {
            name: 'デフォルトリセット',
            desc: '「デフォルトに戻す」ボタンで全パラメータを初期値にリセットできます。'
          },
          {
            name: 'モデル再学習',
            desc: 'パラメータ保存後「モデルを再学習」ボタンで学習を実行します。サイト単位で学習されます。'
          }
        ],
        faq: [
          {
            q: 'どのパラメータを変更すべきですか',
            a: '通常はデフォルト値で十分です。商品数が少ない場合（100件未満）はSVD因子数を下げる、行動データが少ない場合はコンテンツベースの比率を上げるなどの調整が有効です。'
          },
          {
            q: '再学習にかかる時間',
            a: '商品数と行動データ量に依存しますが、通常数秒〜数分です。学習中もレコメンド配信は継続されます。'
          }
        ]
      }
    ]
  },
  {
    group: '🔗 W2Commerce連動',
    items: [
      {
        id: 'w2-mode',
        menu: 'W2Commerce完全連動モード',
        overview:
          'サイトごとに「基本のみ」または「W2Commerce完全連動」のレコメンド使用マスタモードを設定できます。W2Commerce完全連動では14種のW2マスタデータを活用し、より精度の高いレコメンドを実現します。',
        features: [
          {
            name: 'モード切替',
            desc: 'サイト管理画面で「レコメンド使用マスタ」を「基本のみ」または「W2Commerce完全連動」に切り替え。即時反映。'
          },
          {
            name: '対応W2マスタ',
            desc: 'ユーザーマスタ、ユーザー属性、商品マスタ、商品ブランド、商品カテゴリ、商品タグ、商品在庫、受注明細、特集ページ、特集ページカテゴリ、特集ページコンテンツ、商品グループ、商品グループ明細、コンテンツタグの14テーブル。'
          },
          {
            name: 'データ連携方式',
            desc: 'CSV手動アップロード、FTP自動取込の両方に対応。ファイル名プレフィックスでマスタ種別を自動判定。'
          }
        ],
        faq: [
          {
            q: '基本モードとW2連動モードの違いは？',
            a: '基本モードは商品マスタ・受注履歴・行動ログのみでレコメンドします。W2連動モードではW2の14マスタ（ブランド、カテゴリ、在庫バリエーション、クロスセル/アップセル関連商品等）を追加で活用し、より精度の高いレコメンドを実現します。'
          },
          {
            q: 'モードを切り替えると既存データはどうなりますか？',
            a: '既存のレコメンドデータ（行動ログ、受注履歴等）はそのまま保持されます。W2マスタデータは追加のデータソースとして活用されるため、切り替え後にW2マスタを取り込むことで効果が発揮されます。'
          }
        ]
      },
      {
        id: 'w2-tags',
        menu: '配信タグの違い（基本 vs W2）',
        overview: 'W2Commerce完全連動モードでは、配信タグに追加フィールドが含まれます。',
        features: [
          {
            name: '閲覧タグ（商品詳細）',
            desc: '基本: item_id + category_id。W2連動: + item_variation_id（バリエーションID）+ brand_id（ブランドID）'
          },
          {
            name: '閲覧タグ（カテゴリ）',
            desc: '基本: category_id。W2連動: + brand_id（ブランドID）'
          },
          {
            name: 'カートタグ',
            desc: '基本: item_id + price + quantity。W2連動: + item_variation_id'
          },
          {
            name: '購入完了タグ',
            desc: '基本: item_id + price + quantity。W2連動: + item_variation_id。さらにW2商品在庫を自動減算。'
          },
          {
            name: 'クロスセル/アップセル重複排除',
            desc: '商品詳細ページのレコメンドウィジェットでは、W2商品マスタに設定されたクロスセル・アップセル関連商品が自動除外されます。これらは画面上で既に表示されているため、レコメンドとの重複を防止します。BB/OB/similar/personalすべてのロジックに適用。'
          }
        ],
        faq: [
          {
            q: 'W2連動時のタグは自動で変わりますか？',
            a: 'はい。サイトのモードを「W2Commerce完全連動」に設定すると、配信タグ情報画面で表示されるタグが自動的にW2連動版に切り替わります。'
          },
          {
            q: 'item_variation_idは必須ですか？',
            a: '任意です。設定しない場合はバリエーション単位の在庫連動が行われませんが、商品ID単位のレコメンドは正常に機能します。'
          }
        ]
      },
      {
        id: 'w2-logic',
        menu: 'レコメンドロジックの違い',
        overview:
          'W2Commerce完全連動モードでは、3つのレコメンドエンジンすべてが追加データを活用します。',
        features: [
          {
            name: 'コンテンツベース（TF-IDF）',
            desc: '基本: 商品名・カテゴリ・説明文・タグでTF-IDF類似度を計算。W2連動: + W2ブランド名（最大5）+ W2カテゴリ名（最大5）+ 検索キーワード（search_keyword）+ クロスセル/アップセル関連商品ID。ブランドやカテゴリが同じ商品の類似度が自然に高くなります。'
          },
          {
            name: '協調フィルタリング（SVD+KNN）',
            desc: '基本: Event + OrderHistoryからユーザー×商品行列を構築。W2連動: + W2受注明細のデータを追加。さらにクロスセルペアにスコア4.0、アップセルペアにスコア3.5の暗黙的評価を追加。MD担当者の知見がAIに反映されます。'
          },
          {
            name: 'ニューラル（NCF）',
            desc: '基本: 同上のデータでGMF+MLPモデルを学習。W2連動: 協調フィルタリングと同じ追加データで学習。クロスセル/アップセルパターンも学習対象に。'
          },
          {
            name: 'BB/OBロジック',
            desc: '基本: 購入/閲覧ユーザーの共起分析のみ。W2連動: + W2商品マスタのクロスセル関連商品に+3ブースト、アップセル関連商品に+2ブースト。人が設定した関連商品が上位に表示されやすくなります。'
          },
          {
            name: 'クロスセル/アップセル重複排除',
            desc: '商品詳細ページでは、W2商品マスタに設定されたクロスセル（related_product_id_cs1〜cs5）およびアップセル（related_product_id_us1〜us5）の関連商品がレコメンド結果から自動除外されます。これらの商品は商品詳細画面上で既に表示されているため、ウィジェットとの重複を防ぎます。'
          }
        ],
        faq: [
          {
            q: 'クロスセル/アップセルのスコアはどう影響しますか？',
            a: 'W2商品マスタのrelated_product_id_cs1〜cs5（クロスセル）とrelated_product_id_us1〜us5（アップセル）の商品ペアに対して、仮想ユーザーとして暗黙的な購入スコアを追加します。これにより、MD担当者が設定した関連商品がレコメンド結果に反映されます。'
          },
          {
            q: '検索キーワード(search_keyword)はどう使われますか？',
            a: 'カンマ区切りの検索キーワードをスペース区切りに変換してTF-IDFコーパスに追加します。同じキーワードを持つ商品同士の類似度が高くなり、サイト内検索で関連性のある商品がレコメンドされやすくなります。'
          }
        ]
      },
      {
        id: 'w2-filter',
        menu: '商品表示フィルタリングルール',
        overview:
          'W2Commerce完全連動モードでは、レコメンドウィジェットに表示する商品に追加のフィルタリングルールが適用されます。',
        features: [
          {
            name: '検索時表示区分（display_kbn）',
            desc: 'display_kbn = "0"（商品一覧○/商品詳細○）の商品のみ表示。"1"（一覧×/詳細○）や"2"（一覧×/詳細×）の商品は除外されます。'
          },
          {
            name: '有効フラグ（valid_flg）',
            desc: 'valid_flg = "1"（有効）の商品のみ表示。"0"（無効）の商品は除外されます。'
          },
          {
            name: '外部レコメンド利用フラグ（use_recommend_flg）',
            desc: 'use_recommend_flg = "1"（有効）の商品のみ表示。"0"の商品はレコメンド候補から除外されます。'
          },
          {
            name: '表示期間（display_from / display_to）',
            desc: '現在日時が表示期間(FROM)〜表示期間(TO)の範囲内の商品のみ表示。期間外の商品は自動的に除外されます。FROM未設定は制限なし、TO未設定は無期限。'
          },
          {
            name: 'W2商品在庫（バリエーション別）',
            desc: '該当商品IDに紐づく全バリエーション（サイズ・カラー等）の在庫合計が0の場合のみ除外。一部バリエーションに在庫がある場合は表示されます。'
          },
          {
            name: 'フィルタ適用範囲',
            desc: 'BB（この商品を買った人）、OB（この商品を見た人）、パーソナル、類似商品、すべてのレコメンドロジックに適用されます。キャッシュTTL: 60秒。'
          }
        ],
        faq: [
          {
            q: '基本モードのフィルタリングはどうなりますか？',
            a: '基本モードでは、在庫管理テーブルに登録された在庫数が0以下の商品のみ除外されます。W2の追加フィルタリング（表示区分、有効フラグ、レコメンド利用フラグ、表示期間）は適用されません。'
          },
          {
            q: 'フィルタリングのキャッシュはどのくらいですか？',
            a: 'W2除外商品リストは60秒間キャッシュされます。W2マスタを更新した場合、最大60秒後に反映されます。'
          },
          {
            q: '削除フラグ(del_flg)は考慮されますか？',
            a: '現在のフィルタリングルールではdel_flgは直接チェックしていません。ただし、valid_flg="0"に設定されている場合は除外対象になります。del_flgによるフィルタが必要な場合はお問い合わせください。'
          }
        ]
      }
    ]
  },
  {
    group: '🤖 レコメンドエンジン拡張',
    items: [
      {
        id: 'engine-adaptive',
        menu: 'Adaptive（状況適応型）',
        overview:
          'ユーザーの状況（新規/閲覧のみ/購入済/休眠）を自動判定し、最適なレコメンドロジックを切り替えます。ウィジェット1つで全ユーザー状態に対応。',
        features: [
          {
            name: '新規訪問者',
            desc: '行動履歴なし → トレンド（7日）+ カテゴリ人気 + 全体人気。「あなたに合いそうな人気商品」として表示。'
          },
          {
            name: '閲覧のみユーザー',
            desc: '閲覧履歴あり・購入なし → 閲覧商品の類似商品 + トレンドブレンド。直近5商品の類似商品を優先。'
          },
          {
            name: '購入済み会員',
            desc: '購入履歴あり → 3層ハイブリッドAI（協調+ニューラル+コンテンツベース）によるフルパーソナライズ。'
          },
          {
            name: '休眠ユーザー',
            desc: '30日以上未訪問 → トレンド70% + 過去嗜好30%のブレンド。「お帰りなさい。最新のおすすめ」として表示。'
          },
          {
            name: '自動判定ロジック',
            desc: 'visitor_id（クッキー2年保持）でEventテーブルの行動履歴を検索。購入有無・最終行動日時で4状態を自動判定。'
          }
        ],
        faq: [
          {
            q: 'item_idは必要ですか？',
            a: '不要です。visitor_idで自動判定します。ただしcategory_idを渡すとカテゴリ文脈も考慮されます。'
          },
          {
            q: 'どのページに設置すべきですか？',
            a: 'トップページ、マイページに最適です。商品詳細ページにはBB/OB/similarの方が適しています。'
          }
        ]
      },
      {
        id: 'engine-ranking-blend',
        menu: 'Ranking Blend（ランキング×パーソナル）',
        overview:
          'マルチ軸ランキング（閲覧/購入/カート × 24h/7d/30d）にパーソナルスコアをブレンドします。「人気の中からあなたに合いそうな商品」を実現。',
        features: [
          {
            name: 'ランキングスコア',
            desc: '閲覧×1.0、カート×2.0、購入×3.0のアクション重み × 24h×3.0、7d×2.0、30d×1.0の期間重みで複合スコア算出。'
          },
          {
            name: 'ブレンド比率（α自動調整）',
            desc: '新規: α=0.1（ほぼランキング）、閲覧のみ: α=0.4、購入済み: α=0.7（パーソナル寄り）、休眠: α=0.3。'
          },
          {
            name: 'W2ブランド人気ブースト',
            desc: 'W2連動時、人気ブランド（購入数上位5）所属商品にスコア+1.0のブースト。'
          },
          {
            name: 'カテゴリ絞り込み',
            desc: 'category_idを渡すとカテゴリ内ランキングに限定。カテゴリページに最適。'
          }
        ],
        faq: [
          {
            q: 'personalロジックとの違いは？',
            a: 'personalは純粋なAI予測です。ranking_blendは「人気」という社会的証明にパーソナルを混ぜるため、より安心感があり理解されやすいレコメンドになります。'
          }
        ]
      },
      {
        id: 'engine-recently-viewed',
        menu: 'Recently Viewed（最近見たものの続き）',
        overview:
          '直近30日の閲覧履歴から、見た商品の類似商品を返します。直近の閲覧ほど多くの候補を出し、古い閲覧は軽く扱います。',
        features: [
          {
            name: '時間減衰重み',
            desc: '直近の閲覧商品ほど類似商品を多く取得（1番目:4件、2番目:3件、3番目:2件...）。古い興味に引っ張られすぎない設計。'
          },
          {
            name: '理由付き表示',
            desc: '各レコメンドに「〇〇に似ています」という具体的な理由を表示。閲覧元の商品名を含む。'
          },
          { name: 'フォールバック', desc: '閲覧履歴がない場合はトレンド商品にフォールバック。' },
          {
            name: '既閲覧商品の除外',
            desc: '既に見た商品はレコメンド結果から自動除外。新しい出会いを優先。'
          }
        ],
        faq: [
          {
            q: 'マイページとトップページどちらに向いていますか？',
            a: '両方に適しています。マイページでは「最近チェックした商品の続き」、トップでは「あなたへのおすすめ」として表示できます。'
          }
        ]
      }
    ]
  },
  {
    group: '🧠 Media-DRIVER（コンテンツ生成）',
    items: [
      {
        id: 'media-driver-dashboard',
        menu: 'ダッシュボード',
        path: '/sites/:siteId/media/dashboard',
        overview:
          'Media-DRIVER（コンテンツ生成）の週次レポート画面です。市場・競合・自社のコンテンツ状況、サイト調査の進捗、今週のリライト/記事作成/分析数、推奨アクションを統合表示します。enable_content_analysis が有効なサイトで表示されます。',
        features: [
          {
            name: 'コンテンツ状況3カード',
            desc: '市場（トップキーワード）・競合（分析済み数・ギャップ数）・自社（リライト累計・直近4週の更新推移）を表示します。'
          },
          {
            name: 'サイト調査の進捗',
            desc: '競合分析AI・GSC連携・SERP実データの3種の実施状況を表示し、未実施には「実行する」ボタンを出します。'
          },
          {
            name: '今週のサマリー',
            desc: '今週のリライト件数・記事作成数・分析実行数・不足記事数を集計します。'
          },
          {
            name: '推奨アクション集計',
            desc: '未対応/ロードマップ/進行中/承認待ち/完了/却下 の6ステータス別件数を表示します。'
          }
        ],
        faq: [
          { q: '週の起点はいつですか？', a: '月曜開始で「月曜〜現在」を今週として集計します。' },
          {
            q: 'データが少ない場合は？',
            a: 'サイト調査・記事作成・リライトを実行するとダッシュボードに反映されます。まずサイト調査から着手するのがおすすめです。'
          }
        ]
      },
      {
        id: 'ai-tag-master',
        menu: 'AI解析タグ紐づけマスタ',
        overview:
          'W2商品マスタ・特集ページコンテンツからAI（Claude API）が購入に繋がる重要タグを自動抽出し、商品・コンテンツに紐づけて保存します。',
        features: [
          {
            name: 'コンテキストマイニング',
            desc: '単なるキーワード抽出ではなく、ペルソナの悩み（ペインポイント）→ 感情的ベネフィット → 情緒的価値タグの3ステップで深層解析。'
          },
          {
            name: '11カテゴリ分類',
            desc: 'pain_point（悩み）、emotional_gain（感情的ベネフィット）、target_persona（ペルソナ）、buying_motive（購買動機）、usage_scene（利用シーン）、purchase_intent（購入パターン）、feature（特徴）、product_strength（競争優位）、material（素材）、season（季節）、occasion（イベント）。'
          },
          {
            name: 'CVRデータ照合',
            desc: '購入数上位20%商品の共通キーワードを自動抽出し、AIプロンプトに提供。マッチするタグの重要度を1.3倍にブースト。'
          },
          {
            name: 'Few-Shotプロンプティング',
            desc: '5パターンの成功例（機能→便益変換）をシステムプロンプトに埋め込み、抽出精度を向上。'
          },
          {
            name: 'タグ効果トラッキング',
            desc: '「効果トラッキング」タブでタグ別のCTR/CVR/閲覧数/購入数を30日間計測。effectiveness_score（CVR×0.6 + CTR×0.4 × 重要度）でランキング。'
          },
          {
            name: 'レコメンドプレビュー',
            desc: 'タグをクリックすると紐づき商品をカード形式でプレビュー表示。同カテゴリの関連タグもバッジ表示。'
          },
          {
            name: '手動タグキュレーション',
            desc: 'インライン編集でタグ名・カテゴリ・重要度スコアを修正可能。不要なタグは個別削除（却下）。'
          },
          {
            name: '差分解析',
            desc: '「差分解析（新規のみ）」ボタンで、既存タグがない新商品のみをAI解析。既存タグにマージして追記。全件再解析が不要。'
          }
        ],
        faq: [
          {
            q: 'AI解析にかかる時間は？',
            a: '商品10件あたり約5-10秒です。100件の商品で1-2分程度。バックグラウンドで実行されるため、画面操作は継続できます。'
          },
          {
            q: 'タグはレコメンドにどう使われますか？',
            a: '現在はコンテンツベースエンジンのTF-IDFコーパスに反映されます。今後、タグベースの直接マッチングレコメンドも予定しています。'
          },
          {
            q: '差分解析と全件解析の違いは？',
            a: '全件解析は既存タグをすべて削除して再生成します。差分解析は既存タグを保持し、新商品分のみ追加します。日常運用は差分解析がおすすめです。'
          }
        ]
      },
      {
        id: 'feature-rewrite',
        menu: '特集リライト判定',
        overview:
          'W2特集ページコンテンツのSEO診断・改善点・アクションプランをAIが生成し、タグ・カテゴリの変更も示唆します。',
        features: [
          {
            name: 'SEO診断（5項目100点満点）',
            desc: 'タイトル品質・メタディスクリプション・コンテンツ構造・内部リンク・UXエンゲージメントの5項目で採点。パフォーマンスデータ（アクセス数・滞在時間）も加味。'
          },
          {
            name: '改善点リスト',
            desc: '優先度（high/medium/low）付きで問題点・改善提案・期待効果をリストアップ。'
          },
          {
            name: 'アクションプラン',
            desc: '優先順序付きステップ。工数（small/medium/large）とインパクト（high/medium/low）を明示。'
          },
          {
            name: 'タグ変更示唆',
            desc: '追加すべきコンテンツタグ（緑）と削除検討タグ（赤）を理由付きで提案。'
          },
          {
            name: 'カテゴリ変更示唆',
            desc: '現在のカテゴリから推奨カテゴリへの変更と、追加紐づけカテゴリを提案。'
          },
          {
            name: 'AIリライト案自動生成',
            desc: 'タイトル・メタ・見出しの具体的なリライト文案をAI生成。BEFORE→AFTERの視覚比較。追加セクション・CTA改善案も。'
          },
          {
            name: '一括診断＋優先度マトリクス',
            desc: '全特集ページを一括で簡易診断し、「改善インパクト×工数」の2×2マトリクス（最優先/計画的/余裕時/後回し）で可視化。'
          },
          {
            name: '改善前後の比較トラッキング',
            desc: '診断結果を履歴保存。再診断時に前回との差分（↑+5点/↓-3点）を表示。PDCAサイクルの進捗を可視化。'
          }
        ],
        faq: [
          {
            q: '一括診断はAIを使いますか？',
            a: 'いいえ。一括診断はルールベース（タイトル有無・メタ長さ・コンテンツ数）で高速に簡易スコアを算出します。詳細診断は個別ページごとにAIで行います。'
          },
          {
            q: 'リライト案はそのまま使えますか？',
            a: 'AI生成の文案はあくまで提案です。サイトのトーンやブランドに合わせて調整してください。ただし、SEOの観点でのキーワード配置は参考になります。'
          },
          {
            q: '診断履歴はいつまで残りますか？',
            a: '直近10回分を保持します。リライト後に再診断すると、改善度が数値で確認できます。'
          }
        ]
      },
      {
        id: 'article-creator',
        menu: '特集記事作成',
        overview:
          'キーワード選定から原稿チェック、AI記事生成、コンテンツギャップ分析まで、特集コンテンツ作成をワンストップで支援します。',
        features: [
          {
            name: '① キーワード選定',
            desc: 'キーワードを入力するとAIが月間検索数・競合度・CPC・検索意図・難易度・機会スコアを推定。関連キーワード（ロングテール・共起語・質問型）も提案。キーワードグループ化と記事タイトル案を自動生成。'
          },
          {
            name: '② 原稿チェック',
            desc: 'W2特集ページまたは直接入力した原稿をSEO分析。5項目スコア（文字量・KWカバー率・タグ最適化・読みやすさ・エンゲージメント）+ 不足キーワード・共起語カバー率 + 改善提案 + 記事構成案を生成。'
          },
          {
            name: '③ AI記事生成',
            desc: 'キーワード→構成→本文をワンストップ生成。トーン選択（専門的/カジュアル/フレンドリー）と目標文字数（1000-5000字）を指定。タイトル・メタ・セクション別本文・SEOキーワード・内部リンク提案・CTA提案を出力。全文コピー機能付き。'
          },
          {
            name: '④ コンテンツギャップ分析',
            desc: '既存特集ページと商品カテゴリ・ブランド構成を比較し、記事がない領域をAI検出。カバレッジ分析（十分/部分的/未カバー）+ ギャップ一覧（想定KW・推奨タイトル・関連カテゴリ）+ クイックウィン。'
          },
          {
            name: 'GSC連携（準備中）',
            desc: 'Googleサーチコンソール連携でリアルな検索データを取得予定。現在はAIベースの推定分析で代替。'
          },
          {
            name: 'Googleサジェスト自動展開',
            desc: '入力キーワードから50音展開+バリエーションでGoogle Suggest APIから最大70件の関連キーワードを自動取得。'
          },
          {
            name: 'ワードマップ可視化',
            desc: 'バブルチャートでキーワードを月間検索数×CVEスコアの2軸で配置。クリックで詳細パネルを開く。'
          },
          {
            name: 'CVE（コンバージョン期待値）',
            desc: 'キーワードごとに1-5のCVEスコアを算出。5=購入直結、1=潜在層。Pascal SEOツール同等の指標。'
          }
        ],
        faq: [
          {
            q: 'AI記事生成の品質は？',
            a: 'Claude Sonnet 4で生成するため高品質ですが、事実確認やブランドトーンの調整は必要です。構成案として活用し、人間が仕上げるのが最適です。'
          },
          {
            q: 'コンテンツギャップはどう使うべきですか？',
            a: '月1回の実行がおすすめです。新商品やカテゴリ追加後に実行すると、記事展開の漏れを検出できます。クイックウィンから着手してください。'
          },
          {
            q: '原稿チェックはW2特集ページ以外も使えますか？',
            a: 'はい。「直接入力」モードでタイトルと本文を貼り付ければ、任意のテキストをチェックできます。'
          }
        ]
      },
      {
        id: 'media-analysis',
        menu: 'サイト現状分析',
        path: '/sites/:siteId/media/analysis',
        overview:
          '自社サイトと競合サイトのSEO状態をAIが分析し、SWOT分析・改善アクションを提案する機能。Google Search Console連携による実データ分析にも対応。',
        features: [
          {
            name: '競合比較分析',
            desc: '自社ドメイン + 最大3つの競合ドメインを入力し、推定月間流入数・ランクインキーワード数・ドメインオーソリティを比較。キーワード別の順位比較テーブルを生成。'
          },
          {
            name: 'SWOT分析',
            desc: '自社の強み(Strengths)・弱み(Weaknesses)・機会(Opportunities)・脅威(Threats)をAIが自動分析し、4象限で可視化。'
          },
          {
            name: '改善推奨アクション',
            desc: '優先度(high/medium/low)付きの具体的SEO改善施策を生成。対象キーワードと期待効果を含む。'
          },
          {
            name: 'Google Search Console連携',
            desc: 'Googleアカウント認証（OAuth 2.0）によりGSCデータを直接取得。実際の検索パフォーマンス（キーワード別クリック数・表示回数・CTR・平均順位）に基づく分析。データはキャッシュしない。'
          },
          {
            name: 'GSCサイト選択',
            desc: 'GSC認証後、登録済みサイト一覧から分析対象を選択。分析期間（1-90日）を指定可能。'
          },
          {
            name: '分析履歴・比較',
            desc: '過去の分析結果を保存し、前回との差分（流入数・KW数の増減）を比較表示。入力値保存・共有機能でGEO/LLMO分析と入力情報を共有。'
          }
        ],
        faq: [
          {
            q: 'GSC連携は必須ですか？',
            a: 'いいえ。GSC連携なしでもAI推定による分析が可能です。GSC連携すると実データに基づくより精度の高い分析ができます。'
          },
          {
            q: 'GSCのデータはサーバーに保存されますか？',
            a: 'いいえ。GSCデータは取得する目的のみに使用し、キャッシュ・保存はしません。アクセストークンもインメモリ管理です。'
          },
          {
            q: '競合サイトのデータはどこから取得していますか？',
            a: 'AI（Claude API）による推定分析です。実際の検索エンジンデータではなく、AIの知識に基づく推定値です。'
          }
        ]
      },
      {
        id: 'article-uploads',
        menu: 'アップロード管理',
        path: '/sites/:siteId/media/articles/uploads',
        overview:
          '作成・改善した記事のアップロード申請と承認フローを管理する画面です。申請→承認→公開→W2特集ページへのAPI連携アップロードまでを扱います。enable_article_upload が有効なサイトで利用できます。',
        features: [
          {
            name: 'アップロード申請',
            desc: '記事バージョンを指定してアップロード申請を作成します。アップロード日時を設定できます。'
          },
          {
            name: '承認フロー（投票）',
            desc: '申請に対し承認/却下の投票を行います。承認後にアップロード可能になります。'
          },
          {
            name: 'ステータス管理',
            desc: 'pending / upload_ready / rejected / cancelled / uploaded / upload_failed のステータスで進捗を管理します。'
          },
          {
            name: 'W2特集ページ連携',
            desc: '承認済み記事をW2特集ページへAPI連携でアップロードします。失敗時はエラーメッセージを記録します。'
          }
        ],
        faq: [
          {
            q: 'アップロードが失敗した場合は？',
            a: 'ステータスが upload_failed になり、エラー内容が記録されます。原因を解消後に再申請してください。'
          },
          {
            q: '承認には何人の投票が必要ですか？',
            a: 'サイトの承認設定に従います。承認権限は権限管理の「記事承認権限」で付与します。'
          }
        ]
      },
      {
        id: 'automation-review',
        menu: '推奨アクション管理・分析の自動実行',
        path: '/sites/:siteId/media/action',
        overview:
          'Media-DRIVERの推奨アクションの一覧・承認ワークフロー・ロードマップ管理と、分析の自動実行スケジュールを管理する画面です。',
        features: [
          {
            name: '推奨アクション管理',
            desc: '分析結果から抽出された推奨アクションを一覧・承認し、ロードマップでスケジュール管理します。'
          },
          {
            name: '分析の自動実行',
            desc: '定期分析のスケジュールを管理し、実行履歴を確認します。'
          }
        ],
        faq: [
          {
            q: '自動実行はいつ走りますか？',
            a: '設定したスケジュールに従いバックグラウンドジョブで実行されます。実行履歴で結果を確認できます。'
          }
        ]
      }
    ]
  },
  {
    group: '🌐 GEO/LLMO分析',
    items: [
      {
        id: 'citation-analysis',
        menu: 'サイテーション分析',
        path: '/sites/:siteId/citation-analysis',
        overview:
          'ブランドがWeb上のどこで言及されているかを網羅的に分析し、競合との言及量・カテゴリ分布を比較するGEO/LLMO分析の基礎機能。',
        features: [
          {
            name: 'ブランド言及元分析',
            desc: '自社ブランド名でWeb上の言及元ページをAIが推定。50件以上の言及元をURL・タイトル・説明・ドメイン・カテゴリ付きで一覧表示。'
          },
          {
            name: '競合比較',
            desc: '競合ブランド（1社）を指定して言及量・カテゴリ分布を横並び比較。自社にはない競合の言及元ドメインも抽出。'
          },
          {
            name: 'カテゴリ別集計',
            desc: '言及元を19カテゴリ（ニュース、比較、レビュー、SNS、事例等）に自動分類し、棒グラフで可視化。'
          },
          { name: 'ドメイン別集計', desc: '言及元ドメインの多い順にランキング表示。' },
          {
            name: 'AI戦略分析',
            desc: '分析結果を元にブランディング戦略レポートを自動生成。比較テーブル（3軸）、強み・弱み、具体的アクションプランを提示。'
          },
          {
            name: 'リアルタイムストリーム',
            desc: '分析中にリアルタイムで言及元ページが流れるストリーム表示。進捗率と現在のステップを常時表示。'
          },
          {
            name: '分析履歴',
            desc: '過去の分析結果を保存・閲覧可能。前回との比較（言及数増減）にも対応。'
          }
        ],
        faq: [
          {
            q: '言及元は実際のWebページですか？',
            a: 'AIによる推定です。実在する可能性の高いドメイン・URLで生成されますが、実際にそのURLにアクセスできるとは限りません。'
          },
          {
            q: '分析にどのくらい時間がかかりますか？',
            a: '通常2-3分程度です。進捗はリアルタイムで表示されます。'
          }
        ]
      },
      {
        id: 'share-of-voice',
        menu: 'シェア・オブ・ボイス分析',
        path: '/sites/:siteId/share-of-voice',
        overview:
          'AIに質問したとき、自社ブランドがどれくらいの確率で言及されるかを測定。AI検索における市場占有率（SOV）を定量化する。',
        features: [
          {
            name: 'プロンプト自動生成',
            desc: '入力したカテゴリ（例: ECサイト構築）に基づき、「おすすめの○○は？」「○○を選ぶポイントは？」等の質問を10-15個自動生成。'
          },
          {
            name: 'ブランド言及カウント',
            desc: '各質問に対するAIの回答で自社・競合ブランドが何番目に何回言及されるかを集計。'
          },
          {
            name: 'シェア率算出',
            desc: '全ブランドの言及回数を合計し、自社のシェア率（占有率）をパーセントで算出。円グラフで可視化。'
          },
          {
            name: '競合5社比較',
            desc: '最大5社の競合と同時比較。ブランド別サマリー（言及回数・シェア率・平均順位・TOP3率）をカード表示。'
          },
          {
            name: 'インサイト生成',
            desc: '自社が強いクエリタイプ・弱いクエリタイプを特定し、AI占有率を向上させるための改善提案を生成。'
          },
          {
            name: '入力値保存・共有',
            desc: '自社/競合のブランド情報を保存し、4つのGEO/LLMO分析間で共有して再利用可能。'
          }
        ],
        faq: [
          {
            q: 'シェア率は実際のChatGPTの結果と同じですか？',
            a: 'AIによるシミュレーションです。Claude APIを使用して回答を生成し、その中でのブランド言及を集計しています。実際のChatGPT/Geminiの回答とは異なる場合があります。'
          },
          { q: '競合ブランドは何社まで比較できますか？', a: '最大5社まで同時比較可能です。' }
        ]
      },
      {
        id: 'sentiment-analysis',
        menu: 'センチメント分析',
        path: '/sites/:siteId/sentiment-analysis',
        overview:
          'AIが自社ブランドについて語るとき、ポジティブ・ネガティブ・ニュートラルのどのトーンで語っているかを定量評価する。',
        features: [
          {
            name: '評判系プロンプト生成',
            desc: '「○○の評判は？」「○○のメリット・デメリットは？」等の評判に関する質問を5-8個自動生成。'
          },
          {
            name: 'センチメントスコア',
            desc: '各回答のトーンを0-1.0のスコアで数値化。0.7以上=ポジティブ、0.4-0.7=ニュートラル、0.4未満=ネガティブ。'
          },
          {
            name: '推奨レベル判定',
            desc: 'AIの回答が「強く推奨」「推奨」「中立」「注意」「ネガティブ」のいずれかを5段階で判定。'
          },
          {
            name: 'ポジ/ネガ分析',
            desc: '各回答からポジティブポイント・ネガティブポイント・ニュートラルポイントを抽出。改善すべき懸念点を特定。'
          },
          {
            name: '競合比較',
            desc: '競合ブランドとのセンチメントスコア・推奨レベルを横並び比較。'
          },
          {
            name: '改善提案',
            desc: 'AIの評価を向上させるための具体的なコンテンツ戦略・権威性強化施策を提案。'
          }
        ],
        faq: [
          {
            q: 'センチメントスコアを上げるにはどうすればよいですか？',
            a: '権威性のある第三者評価（レビュー、事例、受賞歴等）を充実させること、明確な差別化ポイントを発信すること、ネガティブ情報（価格、制限等）に対する明確な回答を用意することが有効です。'
          }
        ]
      },
      {
        id: 'query-fanout',
        menu: 'クエリ・ファンアウト分析',
        path: '/sites/:siteId/query-fanout',
        overview:
          'AIとの会話が深まっていく過程で、自社ブランドが「最後まで推奨され続けるか、途中で名前が消えるか」を追跡分析する。',
        features: [
          {
            name: '会話フロー追跡',
            desc: 'メインクエリから始まり、フォローアップ質問を3-4段階自動生成。各深さでの自社ブランドの言及有無・順位を記録。'
          },
          {
            name: '持続性スコア',
            desc: '全会話フローの中で自社が言及された割合を0-1.0のスコアで算出。1.0=全段階で言及、0.0=一度も言及されず。'
          },
          {
            name: '脱落ポイント特定',
            desc: '自社ブランドがAIの推薦から消える段階（例: 「価格比較の段階でdropout」）を特定。'
          },
          {
            name: '深度別リテンションチャート',
            desc: '会話の深さごとに自社の言及有無と競合ブランド数をバーチャートで表示。'
          },
          {
            name: 'タイムライン可視化',
            desc: '会話フローを縦のタイムライン形式で表示。各ノードに質問・回答要約・自社言及の有無をアイコンで表現。'
          },
          {
            name: 'インサイト生成',
            desc: '最も強い段階・最も弱い段階を特定し、AIに最後まで推奨されるための改善提案を生成。'
          }
        ],
        faq: [
          {
            q: 'クエリ・ファンアウトとは何ですか？',
            a: 'ユーザーがAIに最初の質問をした後、「もっと詳しく」「費用は？」「他の選択肢は？」と深掘りしていく過程のことです。この過程で自社が推奨され続けるかが、AI時代のブランド力を測る重要な指標になります。'
          },
          {
            q: '持続性スコアの目安は？',
            a: '0.7以上が良好、0.5以上が普通、0.5未満は改善が必要です。競合と比較して相対的に判断してください。'
          }
        ]
      }
    ]
  },
  {
    group: '📧 メール配信連携',
    items: [
      {
        id: 'mail-tracking',
        menu: 'メールクリック・開封トラッキング',
        overview:
          'メールレコメンドAPIのレスポンスに自動付与されるトラッキング機能です。メールテンプレートに埋め込むだけで、クリック数・開封数が自動計測されます。',
        features: [
          {
            name: 'クリック計測（リダイレクト方式）',
            desc: '各商品のtracking_urlをリンク先に使用。クリック時にMailTrackingLogに記録後、商品ページにリダイレクト。RecommendationClickにも連携（コンバージョン計測用）。'
          },
          {
            name: '開封計測（1pxピクセル方式）',
            desc: 'mail_tracking.open_pixelのHTMLタグをメール末尾に埋込。1px透明GIFで開封を検知。Cache-Control: no-cacheで確実に検知。'
          },
          {
            name: '統計API',
            desc: 'GET /api/sites/{id}/mail-tracking/stats?spec_id=xxx でユニーク開封数・クリック数・クリック率を取得可能。'
          },
          {
            name: '再クリック・再開封の追跡',
            desc: '初回はclick/openイベント、2回目以降はclick_repeat/open_repeatとして別途記録。ユニーク数と総数の両方を計測。'
          }
        ],
        faq: [
          {
            q: 'メール配信システムとの連携方法は？',
            a: '埋め込みコード一覧の「メール用レコメンド」タブに、メールで使える配信ごとのコード（HTML）が表示されます。コード内の {会員ID} などをメール配信の差し込み変数に置き換えて本文に貼ってください。メールを開いた時点のおすすめ商品が画像で表示され、クリック・開封・購入が計測されます。'
          },
          {
            q: '開封計測の精度は？',
            a: 'HTMLメールで画像を表示するクライアントのみ検知可能です。テキストメールやプレビューパネルでの自動表示は検知される場合があります。'
          }
        ]
      }
    ]
  },
  {
    group: '📍 ページ別レコメンドウィジェット',
    items: [
      {
        id: 'page-widgets',
        menu: 'ページ別ウィジェット配置ガイド',
        overview:
          '各ページに最適なレコメンドウィジェットの配置方法と推奨ロジックをまとめています。',
        features: [
          {
            name: 'カートページ',
            desc: 'カートタグの下に配置。spec_id + item_id（カート内の代表商品ID）を指定。推奨ロジック: BB（この商品を買った人はこちらも）。'
          },
          {
            name: '注文完了ページ',
            desc: '購入完了タグの下に配置。spec_id + item_id（購入した代表商品ID）を指定。「次もおすすめ」として表示。推奨ロジック: BB / OB。'
          },
          {
            name: 'マイページ',
            desc: 'マイページのトップに配置。spec_id のみ（item_id不要）。visitor_idから自動パーソナライズ。推奨ロジック: adaptive / personal / recently_viewed。'
          },
          {
            name: 'カテゴリページ',
            desc: 'カテゴリ一覧に配置。spec_id + category_id を指定。推奨ロジック: ranking_blend（カテゴリ内人気×パーソナル）。'
          },
          {
            name: '商品詳細ページ',
            desc: 'ウィジェット管理のspec_idタグを使用。item_id必須。推奨ロジック: BB / OB / similar。W2連動時はクロスセル/アップセル重複排除が自動適用。'
          },
          {
            name: 'トップページ',
            desc: 'adaptive / ranking_blend が最適。新規ユーザーにはトレンド、リピーターにはパーソナル。item_id不要。'
          }
        ],
        faq: [
          {
            q: '1ページに複数ウィジェットを配置できますか？',
            a: 'はい。異なるspec_idのウィジェットを複数配置可能です。例えば商品詳細にBBとsimilarの2つを並べることができます。'
          },
          {
            q: 'ロジックはウィジェットで設定するのですか？',
            a: 'はい。配信タグの推奨ロジックは説明用です。実際のロジックはウィジェット管理画面で設定したdisplay_logicが使われます。'
          }
        ]
      }
    ]
  },
  {
    group: '⭐ UGC Intelligence',
    items: [
      {
        id: 'ugc-dashboard',
        menu: 'ダッシュボード',
        path: '/sites/:siteId/ugc-dashboard',
        overview:
          'UGC Intelligence の全体状況を可視化する画面です。直近30日の主要KPI、ソース別内訳、許諾ステータス、収集トレンドを表示します。enable_review が有効なサイトのみ表示されます。',
        features: [
          {
            name: 'KPIカード',
            desc: '総UGC数・アクティブUGC数・レビュー数・平均評価などの主要指標を30日ベースで表示します。'
          },
          {
            name: 'ソース別内訳',
            desc: 'Instagram / レビュー / スタッフ投稿 / アンバサダー / 動画 / Q&A の6ソースからのUGC比率を横棒グラフで表示します。'
          },
          {
            name: '許諾ステータス分類',
            desc: '許諾済み・拒否・未許諾・期限切れの4ステータス別件数を色分け表示します。'
          },
          { name: '日別収集トレンド', desc: '直近7日間のUGC収集数を日別グラフで表示します。' }
        ],
        faq: [
          {
            q: '集計期間は変更できますか？',
            a: 'ダッシュボードは30日固定です。期間を変えて分析したい場合はCVRダッシュボード等で日数を切り替えてください。'
          },
          {
            q: 'データが表示されません',
            a: 'タグ設置とレビュー収集が行われているか確認してください。投稿・収集データが無い間はKPIが0表示になります。'
          }
        ]
      },
      {
        id: 'ugc-reviews',
        menu: 'レビュー管理',
        path: '/sites/:siteId/ugc-reviews',
        overview:
          '収集したレビューの承認・編集・タグ付けを行う管理画面です。公開/非公開/保留/後で の4ステータス、星評価、ショップ返信、内部タグ、内部メモ、AIスコアを管理します。',
        features: [
          {
            name: 'ステータス4段階管理',
            desc: '公開 / 非公開 / 保留 / 後で をワンクリックで切り替えます。色分けUIで状態を識別できます。'
          },
          {
            name: 'フィルタ・検索',
            desc: 'ステータス・星評価・内部タグ・テキスト（タイトル+本文）で絞り込めます。'
          },
          {
            name: 'レビュー編集 / ショップ返信',
            desc: 'タイトル・本文・星評価を直接編集し、店舗側の返信コメントを記録できます。'
          },
          {
            name: '内部タグ・内部メモ',
            desc: '分類用の内部タグ（複数選択）と顧客非表示のメモを付与できます。'
          },
          {
            name: 'AIスコア表示',
            desc: 'AIが算出した品質スコア（0〜100）を色分け表示します（70以上=緑、40〜70=黄、40未満=赤）。'
          },
          { name: '一括公開', desc: '保留中の複数レビューを一括で公開状態に変更できます。' }
        ],
        faq: [
          {
            q: 'ステータス変更で顧客に通知されますか？',
            a: 'いいえ。ステータスは内部管理用で、顧客への通知は行われません。'
          },
          {
            q: '内部タグは追加・変更できますか？',
            a: '現状は定義済みのタグから選択する方式です。タグ自体の追加にはシステム改修が必要です。'
          }
        ]
      },
      {
        id: 'ugc-mail',
        menu: 'レビュー依頼配信設定',
        path: '/sites/:siteId/ugc-mail',
        overview:
          '購入者へのレビュー依頼タイミングを商品特性ごとに最適化する設定画面です。サイト共通から商品ID直接指定まで7段階の優先度ルールで、初回送信・フォローアップの日数を制御します。',
        features: [
          {
            name: '7段階のルールタイプ',
            desc: 'サイト全体 / ブランド別 / カテゴリ別（階層対応）/ 商品タグ別 / 商品アイコン別 / 価格帯別 / 商品ID直接指定。下位ほど優先度が高くなります。'
          },
          {
            name: '遅延日数設定',
            desc: '出荷完了後の初回送信日数（既定3日）・フォローアップ1（既定7日）・フォローアップ2（既定14日）を設定します。0で「送らない」を表現できます。'
          },
          {
            name: 'カテゴリ階層対応',
            desc: '上位カテゴリで設定すると下位にも適用されます。下位で個別ルールを作れば上書きできます。'
          },
          {
            name: '価格帯クラスタリング',
            desc: '価格帯を自動で5分類し、価格帯別の依頼タイミングを設定できます。'
          },
          {
            name: 'ルール解決テスト',
            desc: '商品IDを入力して、どのルールが適用され何日後に送信されるかを事前確認できます。'
          }
        ],
        faq: [
          {
            q: '複数ルールにマッチした場合は？',
            a: '優先度評価により最も優先度の高いルール1件が適用されます。複数同時適用は起こりません。'
          },
          {
            q: '既に出荷済みの顧客にも新ルールが適用されますか？',
            a: '送信前に評価されるため、未送信分には新ルールが適用されます。既に送信済みのメールは再送されません。'
          }
        ]
      },
      {
        id: 'ugc-gifting',
        menu: 'ギフティング',
        path: '/sites/:siteId/ugc-gifting',
        overview:
          'UGC収集を促進するギフティングキャンペーンの管理画面です。対象顧客の自動抽出・メール送信・応募者管理・当選選定・出荷管理・ファネル可視化を行います。AIによるターゲット提案機能を備えます。',
        features: [
          {
            name: 'ターゲット選定・プレビュー',
            desc: '購入回数・購入期間・金額・会員ランク・性別・メルマガ許可など複数条件で対象を絞り込み、該当人数を即座に確認できます。'
          },
          {
            name: 'AIターゲット提案',
            desc: '希望対象人数を指定するとAIが複数のターゲットパターンを提案します。'
          },
          {
            name: 'キャンペーン作成',
            desc: 'キャンペーン名・商品・当選者数・選定方法（抽選/先着/手動）・メール遅延（初回/フォローアップ1/2）を設定します。'
          },
          {
            name: '応募者管理・当選選定',
            desc: '応募者を一覧表示し、抽選または先着で当選者を自動決定します。'
          },
          { name: '出荷管理', desc: '出荷済みマーク・出荷CSV出力で発送業務を支援します。' },
          {
            name: 'ファネル可視化・リマインド',
            desc: '送信→応募→当選→出荷→QR読取→レビュー投稿の6段階ファネルを表示し、投稿期限超過者にリマインドを送信します。'
          }
        ],
        faq: [
          { q: '先着選定の順序は？', a: '応募日時の昇順で判定し、先着N人が当選します。' },
          {
            q: '出荷CSVには何が含まれますか？',
            a: '当選者の顧客情報と出荷管理データが出力されます。発送業者への取り込みに利用できます。'
          }
        ]
      },
      {
        id: 'ugc-loyalty',
        menu: 'ロイヤルティ',
        path: '/sites/:siteId/ugc-loyalty',
        overview:
          'UGC投稿活動に対しポイントを付与し、会員ランク（General / Silver / Gold / Platinum）を判定する機能です。ポイント設定・ランク閾値・トップ会員を管理します。',
        features: [
          {
            name: 'ポイント設定',
            desc: 'レビュー投稿・写真付きレビュー・Q&A回答・SNSシェアの4種にポイント数を設定します。'
          },
          {
            name: 'ランク閾値設定',
            desc: 'Silver / Gold / Platinum 昇格に必要なポイント数を設定します。'
          },
          {
            name: 'トップ会員表示',
            desc: 'ポイント順TOP10の会員（投稿数・合計ポイント・現在ランク）を表示します。'
          }
        ],
        faq: [
          { q: 'ランク降格はありますか？', a: 'いいえ。ランクは昇格一方向の設計です。' },
          {
            q: 'ポイント設定変更は既存ユーザーに遡及しますか？',
            a: '新ルールは新規投稿から適用されます。既存の合計ポイントの遡及再計算は行われません。'
          }
        ]
      },
      {
        id: 'ugc-compliance',
        menu: 'GDPR/DSA（コンプライアンス）',
        path: '/sites/:siteId/ugc-compliance',
        overview:
          'EU GDPR「忘れられる権利」に基づき、ユーザーの個人データを一括削除する機能です。許諾ステータス集計も表示します。',
        features: [
          { name: '許諾ステータス集計', desc: '総UGC・許諾済み・拒否・許諾率を表示します。' },
          {
            name: '個人データ削除リクエスト',
            desc: 'メールアドレスまたはcustomer_idを指定して、UGC・レビュー・メールログ・ロイヤルティのデータを削除します。'
          },
          { name: '完了通知', desc: '削除完了日時と件数内訳を表示します。' }
        ],
        faq: [
          {
            q: '購入履歴も削除されますか？',
            a: '削除対象はUGC・レビュー・メールログ・ロイヤルティです。購買データ（受注履歴）は対象外です。'
          },
          {
            q: '削除後に同じユーザーが再投稿できますか？',
            a: 'できます。削除は過去データのみで、アカウント自体は残ります。'
          }
        ]
      },
      {
        id: 'ugc-cvr',
        menu: 'CVRダッシュボード（効果・分析）',
        path: '/sites/:siteId/ugc-cvr',
        overview:
          'UGC表示の有無でCVRがどう変わるかを分析する画面です。「UGC表示ありCVR」「未表示CVR」「改善率」を比較します。期間（7/30/90日）を選択できます。',
        features: [
          { name: 'CVR3分割表示', desc: 'UGC表示あり / 表示なし / 改善率の3カードを表示します。' },
          {
            name: 'View→CVファネル',
            desc: '各CVRについてviews→purchasesのファネルを数値表示します。'
          },
          {
            name: 'トップUGCランキング',
            desc: 'CVR貢献度Top5のUGCをインプレッション・クリック・購入の数値とともに表示します。'
          },
          {
            name: 'SKU×流入経路マトリクス',
            desc: '商品ごとにutm_source別のCVRをクロス集計します。'
          }
        ],
        faq: [
          {
            q: '「効果・分析」には他にどんな画面がありますか？',
            a: 'CVRダッシュボードのほか、SEOダッシュボード・媒体別KPIレポート・A/Bテスト管理・VOC/NPS分析・ベンチマーク比較・コンテンツ鮮度アラートがあります。'
          },
          {
            q: '改善率はどう計算されますか？',
            a: '(UGC表示ありCVR − 未表示CVR) ÷ 未表示CVR × 100 で算出します。'
          }
        ]
      },
      {
        id: 'ugc-ai-score',
        menu: 'AIスコアリング設定',
        path: '/sites/:siteId/ugc-ai-score',
        overview:
          'AIによるUGC品質の自動採点設定画面です。6軸（時間軸・ライフスタイル・推薦意向・具体的効果・テキスト品質・画像品質）のウェイト設定、テスト実行、バッチスコアリング、履歴表示を行います。',
        features: [
          { name: '自動スコアリングON/OFF', desc: '有効化すると毎日定刻にバッチ実行されます。' },
          {
            name: '6軸ウェイト設定',
            desc: '各評価軸のウェイトをスライダーで調整します。デフォルトへのリセットも可能です。'
          },
          {
            name: 'バッチ/テスト実行',
            desc: '最大50件の一括スコアリング、または1件のテストスコアリングを実行できます。'
          },
          {
            name: 'プロンプト編集・履歴',
            desc: 'システムプロンプトをカスタマイズでき、スコアリング履歴（スコア・カテゴリ・推論理由・日時）を確認できます。'
          }
        ],
        faq: [
          { q: 'バッチは何件まで処理しますか？', a: '1回の実行で最大50件です。' },
          {
            q: 'スコアはどこで使われますか？',
            a: 'レビュー管理画面でAIスコアとして表示され、選別の判断材料になります（70点以上が高品質目安）。'
          }
        ]
      },
      {
        id: 'ugc-misc',
        menu: 'その他の機能',
        path: '/sites/:siteId/ugc-*',
        overview: 'UGC Intelligence には上記のほか、収集・配信・連携の各種機能があります。',
        features: [
          {
            name: 'Instagram UGC / マルチCH配信 / スタッフ投稿',
            desc: 'Instagram自動収集、メール・LINE・プッシュへのマルチチャネル配信、店舗スタッフの公式投稿管理。'
          },
          {
            name: 'Q&A管理 / 動画コマース',
            desc: 'ユーザーQ&A・FAQ管理、動画UGCのショッパブルビデオ化。'
          },
          {
            name: '配信タグ・表示設定',
            desc: '埋め込みタグ設定、UGC 商品タグ付与、サイト表示枠ごとの内容とデザイン設定。'
          },
          {
            name: 'Instagram設定',
            desc: 'Instagram Business Account の接続、監視ハッシュタグと自動収集の頻度、ブランド情報（ブランド名・ロゴ・デフォルト許諾メッセージ）。'
          },
          { name: '設定ガイド', desc: 'UGC Intelligence のセットアップ手順・チュートリアル。' }
        ],
        faq: [
          {
            q: 'これらの機能は契約に含まれますか？',
            a: 'UGC Intelligence（enable_review）が有効なサイトで利用できます。メニューの表示はサイト権限設定にも依存します。'
          }
        ]
      }
    ]
  },
  {
    group: '🛒 Findee（AI購買支援）',
    items: [
      {
        id: 'findee-dashboard',
        menu: 'ダッシュボード',
        path: '/sites/:siteId/ai-purchase-assist',
        overview:
          'Findee（AI購買支援）の効果測定画面です。会話数・利用CVR・GMV・満足度・LLMコスト・推薦商品ランキングを表示します。期間（7/30/90日）を選択できます。enable_ai_purchase_assist が有効なサイトのみ表示されます。',
        features: [
          {
            name: 'KPI4カード',
            desc: '利用（会話数・UU）/ ビジネス（CVR・GMV・推薦クリック経由CVR）/ 品質（満足度・👍👎）/ アラート（問題件数・コスト）を前期間比とともに表示します。'
          },
          { name: '日次推移チャート', desc: '会話数とGMVの2軸折れ線グラフを表示します。' },
          {
            name: '経由 vs 未経由比較',
            desc: 'Findee経由と未経由の会話数・購買数・CVR・GMV・AOVを比較します。'
          },
          {
            name: '頻出質問・推薦商品TOP10',
            desc: '会話で多い質問と参照された商品の順位を表示します。「分かりません」率が高い場合は警告します。'
          },
          {
            name: 'LLMコスト内訳',
            desc: 'モデル×段階別のトークン数・コスト・キャッシュヒット率を集計します。'
          }
        ],
        faq: [
          {
            q: '「分かりません」率が高いときは？',
            a: 'ナレッジ管理で固定回答を登録し、LLMより優先マッチさせることで削減できます。AIプロファイルのプロンプト見直しも有効です。'
          },
          {
            q: 'キャッシュヒット率が低いときは？',
            a: '同じ質問が繰り返されていない可能性があります。ナレッジで固定化するとコスト削減につながります。'
          }
        ]
      },
      {
        id: 'findee-campaigns',
        menu: '配信設定',
        path: '/sites/:siteId/ai-purchase-assist/campaigns',
        overview:
          'Findeeウィジェットの配信ルール（対象ページ・起動条件・デザイン・AI動作）を管理する画面です。配信名・ステータス・対象モード・効果指標を一覧で確認できます。',
        features: [
          {
            name: '配信サマリーKPI',
            desc: '公開中の配信数・起動回数・経由CVR・経由GMVを前期間比とともに表示します。'
          },
          { name: 'フィルタ・検索', desc: '配信名・対象モード・期間で絞り込めます。' },
          {
            name: '対象モード設定',
            desc: '全ページ / 商品詳細 / カテゴリ / URL指定 から起動対象を選べます。'
          },
          { name: 'ステータス管理', desc: '下書き → 公開中 → 停止中 の遷移で配信を制御します。' },
          {
            name: '対話テスト',
            desc: '配信設定（プロファイル+ベクトル検索+デザイン）で実環境同様の対話をテストできます。'
          }
        ],
        faq: [
          {
            q: '「起動」とは何ですか？',
            a: 'Findeeウィジェットが顧客に表示された回数です。ページの起動条件を満たした場合にカウントされます。'
          },
          {
            q: '複数の配信を同時に走らせられますか？',
            a: 'はい。A/Bテストのため複数配信を並行実行し、訪問者を振り分けられます。'
          }
        ]
      },
      {
        id: 'findee-rich-ui',
        menu: 'リッチUIデザイナー',
        path: '/sites/:siteId/ai-purchase-assist/rich-ui',
        overview:
          'Findeeの応答UIを9種類のコンポーネント（商品ギャラリー・Heroカード・比較テーブル・レーダーチャート・パーソナライズ・二択カード・フィルタスライダー・レビューハイライト・バンドル）から選んで配信単位で設定する画面です。',
        features: [
          {
            name: '共通テーマ設定',
            desc: 'アクセント色・背景・文字色・角丸・フォントサイズ・自由CSSを設定します。'
          },
          {
            name: '10コンポーネント設定',
            desc: '各コンポーネントの表示有無・専用オプション・カスタムCSSを設定します。'
          },
          {
            name: 'プリセット管理',
            desc: 'Hero / Bundle / QuickChoice のプリセットを保存・呼び出しできます。お気に入り登録も可能です。'
          },
          { name: '配信単位適用', desc: '対象配信を選択し、その配信のみに設定を反映します。' }
        ],
        faq: [
          {
            q: 'カスタムCSSはどこに適用されますか？',
            a: '各コンポーネントのDOM要素に適用されます。他コンポーネントへの影響に注意してください。'
          },
          {
            q: '変更を元に戻せますか？',
            a: '保存前であればキャンセルで破棄できます。保存済みのバージョン管理（ロールバック）はありません。'
          }
        ]
      },
      {
        id: 'findee-ab-tests',
        menu: 'A/Bテスト',
        path: '/sites/:siteId/ai-purchase-assist/ab-tests',
        overview:
          "Findee配信のA/Bテストを管理する画面です。カイ二乗検定とCohen's hで有意性を判定します。テスト種別はON/OFF（表示有無）・配信A vs B・単一要素変更の3種です。",
        features: [
          {
            name: 'テスト種別3種',
            desc: 'ON/OFF（導入効果証明）/ 配信A vs B / 単一要素変更 を選べます。'
          },
          { name: '分割比設定', desc: 'AとBの振り分け比率を設定します。' },
          {
            name: '統計分析',
            desc: "カイ二乗値・Cohen's h・p値・有意水準（α=0.05）の判定結果を表示します。早期停止判定にも対応します。"
          },
          {
            name: 'テスト詳細ビュー',
            desc: '各バリアントの露出数・CV数・CVR・差分・有意性を表示します。'
          }
        ],
        faq: [
          {
            q: 'テスト実行中に配信を編集できますか？',
            a: '実行中のテストは編集できません。中断してから編集してください。'
          },
          {
            q: '有意差はどう判定されますか？',
            a: "カイ二乗検定でp値を算出し、α=0.05を下回れば有意と判定します。効果量はCohen's hで評価します。"
          }
        ]
      },
      {
        id: 'findee-knowledge',
        menu: 'ナレッジ・回答誘導',
        path: '/sites/:siteId/ai-purchase-assist/knowledge',
        overview:
          'LLMより優先する「固定回答」をQA形式で登録する画面です。カテゴリ分類・有効無効トグル・ヒット数集計・マッチテストを行います。',
        features: [
          {
            name: 'ナレッジ一覧',
            desc: '質問・回答・カテゴリ・有効状態・累計ヒット数を表示します。'
          },
          {
            name: 'カテゴリ分類',
            desc: '返品・配送・営業時間・価格・ブランド・商品情報・ポリシー・カスタムの分類で絞り込めます。'
          },
          { name: '有効/無効トグル', desc: '無効化するとLLMにフォールバックします。' },
          { name: 'マッチテスト', desc: 'テスト質問に対し最適な固定回答が返るか確認できます。' },
          {
            name: '自動候補生成・ヒット数追跡',
            desc: 'LLMが回答できなかった頻出質問を候補として抽出し、固定回答のヒット数でコスト削減効果を可視化します。'
          }
        ],
        faq: [
          {
            q: 'ナレッジ更新は進行中の会話に影響しますか？',
            a: '完了済みの会話には影響しません。新規会話から新ルールが適用されます。'
          },
          {
            q: '固定回答とLLM回答の優先順位は？',
            a: 'マッチした固定回答が優先され、無い場合はLLMが回答します。'
          }
        ]
      },
      {
        id: 'findee-misc',
        menu: 'その他の機能',
        path: '/sites/:siteId/ai-purchase-assist/*',
        overview: 'Findeeには上記のほか、会話分析・安全管理・データ管理の機能があります。',
        features: [
          {
            name: '会話履歴・分析',
            desc: '過去の会話ログとユーザー分析。購買連動（24時間以内購入・推薦クリック経由）を追跡します。'
          },
          {
            name: 'フィードバック管理',
            desc: 'ユーザーからのフィードバック・改善提案を管理します。'
          },
          {
            name: 'AIプロファイル / NGルール',
            desc: 'ボットの人格・トーン設定、NGワード・禁止トピックの設定と検知ログ。'
          },
          {
            name: 'ベクトル化データ管理',
            desc: '埋め込みベクトル・RAGデータソースの管理。UGCレビュー本文をベクトル化し、過去レビューからAIが回答できます。'
          }
        ],
        faq: [
          {
            q: 'NGワードはどう機能しますか？',
            a: '設定したNGワード・禁止トピックに該当する応答を抑制し、検知ログに記録します。'
          },
          {
            q: 'レビューをFindeeの回答に活用できますか？',
            a: 'はい。ベクトル化データ管理でUGCレビューをベクトル化すると、「サイズ感は?」等の質問に過去レビューから回答できます。'
          }
        ]
      }
    ]
  }
]

const SupportSpecPage = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const filteredSpecs = MENU_SPECS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (!searchQuery) return true
      const q = searchQuery.toLowerCase()
      return (
        item.menu.toLowerCase().includes(q) ||
        item.overview.toLowerCase().includes(q) ||
        item.features.some(
          (f) => f.name.toLowerCase().includes(q) || f.desc.toLowerCase().includes(q)
        ) ||
        item.faq.some((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q))
      )
    })
  })).filter((group) => group.items.length > 0)

  return (
    <Main type={MainLayoutType.FULL} bottomSpace>
      <PageTitle
        title='サポート仕様'
        description='各メニューの操作概要と機能仕様（社内サポート用）'
      />

      {/* 検索バー */}
      <div className='mb-4 flex gap-2'>
        <input
          className='border-secondary bg-surface text-primary placeholder:text-disabled focus:border-focus flex-1 rounded-xl border px-4 py-2.5 text-sm outline-none'
          placeholder='メニュー名、機能名、キーワードで検索...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className='border-caution bg-surface-caution text-caution rounded-xl border px-4 py-2 text-sm font-semibold'
          >
            クリア
          </button>
        )}
      </div>

      {/* クイックナビ */}
      <div className='border-secondary bg-surface shadow-area mb-6 flex flex-wrap gap-x-6 gap-y-3 rounded-xl border p-4'>
        {MENU_SPECS.map((group) => (
          <div key={group.group} className='flex flex-col gap-1'>
            <span className='text-tertiary text-[11px] font-bold tracking-wide uppercase'>
              {group.group}
            </span>
            {group.items.map((item) => (
              <a
                key={item.id}
                href={`#spec-${item.id}`}
                className='text-emphasis cursor-pointer py-0.5 text-xs hover:underline'
                onClick={(e) => {
                  e.preventDefault()
                  setActiveSection(item.id)
                  document.getElementById(`spec-${item.id}`)?.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                {item.menu}
              </a>
            ))}
          </div>
        ))}
      </div>

      {/* 仕様カード */}
      {filteredSpecs.map((group) => (
        <div key={group.group}>
          <h2 className='border-secondary text-secondary mt-6 mb-3 border-b pb-2 text-base font-bold'>
            {group.group}
          </h2>
          {group.items.map((item) => (
            <div
              key={item.id}
              id={`spec-${item.id}`}
              className='border-secondary bg-surface shadow-area mb-3 overflow-hidden rounded-xl border'
            >
              {/* ヘッダー */}
              <div
                className={`hover:bg-surface-hover flex cursor-pointer items-center justify-between px-5 py-4 transition-colors ${activeSection === item.id ? 'border-secondary border-b' : ''}`}
                onClick={() => setActiveSection((prev) => (prev === item.id ? null : item.id))}
              >
                <div>
                  <h3 className='text-primary mb-1 text-base font-bold'>{item.menu}</h3>
                  <p className='text-secondary m-0 text-sm leading-relaxed'>{item.overview}</p>
                </div>
                <span className='text-tertiary ml-3 shrink-0 text-sm'>
                  {activeSection === item.id ? '▾' : '▸'}
                </span>
              </div>

              {/* 詳細 */}
              {activeSection === item.id && (
                <div className='px-5 py-4'>
                  {/* 機能仕様 */}
                  <div className='mb-5'>
                    <h4 className='border-secondary text-primary mb-2.5 border-b pb-1.5 text-sm font-bold'>
                      機能仕様
                    </h4>
                    <table className='w-full border-separate border-spacing-y-1'>
                      <thead>
                        <tr>
                          <th className='text-tertiary w-40 pb-1 text-left text-[11px] font-bold tracking-wide uppercase'>
                            機能名
                          </th>
                          <th className='text-tertiary pb-1 text-left text-[11px] font-bold tracking-wide uppercase'>
                            説明
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.features.map((f, i) => (
                          <tr key={i} className='rounded-lg'>
                            <td className='bg-surface-hover text-primary rounded-l-lg px-3.5 py-3 align-top text-sm font-semibold'>
                              {f.name}
                            </td>
                            <td className='bg-surface-hover text-secondary rounded-r-lg px-3.5 py-3 text-sm leading-relaxed'>
                              {f.desc}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* FAQ */}
                  {item.faq.length > 0 && (
                    <div className='mb-5'>
                      <h4 className='border-secondary text-primary mb-2.5 border-b pb-1.5 text-sm font-bold'>
                        よくある問い合わせ (FAQ)
                      </h4>
                      {item.faq.map((f, i) => (
                        <div
                          key={i}
                          className='border-secondary bg-surface-hover mb-2 rounded-xl border px-3.5 py-2.5'
                        >
                          <div className='text-primary mb-1 text-sm font-bold'>Q. {f.q}</div>
                          <div className='text-secondary text-sm leading-relaxed'>A. {f.a}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* パス情報 */}
                  {item.path && (
                    <div className='bg-surface-hover flex items-center gap-2 rounded-lg px-3.5 py-2'>
                      <span className='text-tertiary text-[11px] font-bold tracking-wide uppercase'>
                        画面パス:
                      </span>
                      <code className='bg-emphasis text-emphasis rounded px-2 py-0.5 font-mono text-xs'>
                        {item.path}
                      </code>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </Main>
  )
}

export default SupportSpecPage
